import { Vector3 } from 'three';
import { TrackRuntime } from '../track/runtime';
import { Frame } from '../track/frames';
import { mergeMeshes, sweepProfile, SweepMesh } from '../geometry/sweep';
import {
  pinMesh,
  sleeveMesh,
  sleeveCavityMesh,
  solidJunctionMesh,
  trackPinMesh,
  trackSleeveMesh,
  trackSleeveCavityMesh,
  jointBasis,
  JOINT_PIN_LENGTH_M,
  JOINT_SOCKET_PLUG_M,
  JOINT_PIN_RADIUS_M,
  beamFitPinRadius,
  beamFitKeyFlat,
  boxPinMesh,
  boxSleeveMesh,
  boxSleeveCavityMesh,
  type2JointExtents,
} from './joints';
import {
  fixPrintSolid,
  unionSweeps,
} from './stlFixer';
import { proposeTrackCuts, ObbMm, obbMaxSideMm, pointsObbMm, printChunkFitLimitMm, minTrackChunkArcM } from './split';
import { buildWholeMeshSources, HubSpec, MemberSpec } from './wholeMesh';
import {
  HUB_MATCH_M,
  FOOTER_MATCH_M,
  cylinderMesh,
  flatFooterMesh,
  meshPoints,
  prepareSupportPrimitives,
  jointOnFooterPad,
  endOnFooterPad,
} from './supportPrims';
import {
  normalizePrintSettings,
  PrintAtom,
  PrintJob,
  PrintPart,
  PrintSettings,
  printMmToWorldMetres,
} from './types';
import {
  FLAT_TERRAIN,
  generateSupports,
  SupportParams,
  Terrain,
} from '../supports/generate';
import { SupportOverride, TrackSupportPrimitive } from '../supports/types';
import { segmentSegmentClosest } from '../supports/bvh';
import {
  buildGearRackMeshes,
  normalizeTrackShapeSettings,
  printTrackProfile,
  trackLoopsForShape,
  type2BeamJointGeom,
  Type2BeamJointGeom,
  TrackShapeSettings,
} from '../geometry/gearRack';

export {
  ensureBeamJointHubs,
  dedupeJointHubs,
  prepareSupportPrimitives,
  flatFooterMesh,
  jointOnFooterPad,
} from './supportPrims';

/** Distance from rail below which a member end is treated as track-attached. */
const TRACK_ATTACH_MAX_M = 1.25;

/** Shortest half a beam may be cut down to — below this a stump cannot take a click-fit. */
const MIN_HALF_BEAM_M = Math.max(JOINT_PIN_LENGTH_M * 2, printMmToWorldMetres(8));

/**
 * A pack boundary must always fall halfway along a beam, never on a joint.
 * Beams shorter than two halves cannot be cut at all — the joints at both ends
 * then have to travel together in one part.
 */
function beamCanBeHalved(lengthM: number): boolean {
  return lengthM >= MIN_HALF_BEAM_M * 2 - 1e-6;
}

export interface TrackPinSpec {
  origin: Vector3;
  /** Pin axis from the support into the track (male points this way). */
  dir: Vector3;
}

interface HubPinSpec {
  origin: Vector3;
  dir: Vector3;
  hubKey: string;
  applied?: boolean;
}

interface ChainCutSpec {
  origin: Vector3;
  dir: Vector3;
  /** Pin/sleeve radius sized to fit inside the beam. */
  pinRadius: number;
  applied?: boolean;
}

/** Atomic support fragment after hub/beam splits (mid-beam fit cuts, never sphere/footer cuts). */
export interface SupportPiece {
  mesh: SweepMesh;
  points: Vector3[];
  s: number;
  additives?: SweepMesh[];
  subtract?: SweepMesh[];
  trackPin?: TrackPinSpec;
  hubPos?: Vector3;
  /** Sphere radius (matches connected beam diameter). */
  hubRadius?: number;
  /** Ground-pad junction (treated like a hub for packing — never split here). */
  footerPos?: Vector3;
  hubPins?: HubPinSpec[];
  memberEnds?: { pos: Vector3; along: Vector3 }[];
  /** Horizontal footer glue-face at memberEnds[0] / [1]. */
  footerCapEnd?: [boolean, boolean];
  /** Mean beam radius for mid-beam connector sizing. */
  beamRadius?: number;
  chainId?: string;
  chainIndex?: number;
  chainPin?: ChainCutSpec;
  chainSocket?: ChainCutSpec;
  /**
   * Remainder half past a track-owned elbow stump (end of beam 2).
   * Support chunk seeding prefers these before other leftovers.
   */
  fromTrackCut?: boolean;
}

/** Joint (+ optional footer) bundled with every half-beam that meets it — pack atom. */
export interface SupportPart {
  pieces: SupportPiece[];
  junction: SupportPiece | null;
}

interface TrackChunkDraft {
  s0: number;
  s1: number;
  meshes: SweepMesh[];
  subtract: SweepMesh[];
}

/** Progress update from {@link generatePrintJob}. `fraction` is 0..1. */
export interface PrintProgress {
  fraction: number;
  message: string;
}

export type PrintProgressCallback = (p: PrintProgress) => void;

/** Notify UI and yield so the browser can paint between heavy stages. */
async function reportProgress(
  onProgress: PrintProgressCallback | undefined,
  fraction: number,
  message: string,
): Promise<void> {
  onProgress?.({ fraction: Math.min(1, Math.max(0, fraction)), message });
  await new Promise<void>((r) => setTimeout(r, 0));
}

/**
 * Whole → cut → connect print pipeline:
 * 1. Mesh the entire coaster as one model (continuous track + full supports).
 * 2. Cut the track solid contiguously from s=0 until it no longer fits; keep cuts
 *    a configurable clearance from support connections; always add male/female.
 * 3. Detach supports at hubs; half-split each joint span (then fit-split if needed).
 * 4. Always keep stub + elbow (beams 1–2) on the owning track chunk. Keep
 *    adding connected joint-parts onto that chunk while they still fit.
 *    Never pull in nearby non-contiguous track that merely shares a spatial box.
 *
 * Support packs obey one hard rule: **a boundary between parts is always a cut
 * halfway along a beam, never a joint**. A joint sphere / ground pad therefore
 * ships with every beam end that meets it, and two joints bridged by a beam too
 * short to halve ({@link beamCanBeHalved}) travel together in one part.
 */
export async function generatePrintJob(
  runtime: TrackRuntime,
  settingsIn: Partial<PrintSettings>,
  opts?: {
    supportsEnabled?: boolean;
    supportParams?: SupportParams;
    supportOverrides?: SupportOverride[];
    terrain?: Terrain;
    supportPrimitives?: TrackSupportPrimitive[];
    trackShape?: Partial<TrackShapeSettings> | null;
    onProgress?: PrintProgressCallback;
  },
): Promise<PrintJob> {
  const settings = normalizePrintSettings(settingsIn);
  const trackShape = normalizeTrackShapeSettings(opts?.trackShape);
  const onProgress = opts?.onProgress;
  const warnings: string[] = [];
  const log: string[] = [];
  const logLine = (line: string) => {
    log.push(line);
  };
  const L = runtime.totalLength;
  if (L < 0.05 || runtime.centerline.track.nodes.length < 2) {
    const msg = 'Track is too short to print.';
    return { settings, cuts: [], parts: [], atoms: [], warnings: [msg], log: [msg] };
  }

  logLine('=== RCSimFable print generation log ===');
  logLine(
    `Track: closed=${runtime.closed} length=${L.toFixed(3)} m nodes=${runtime.centerline.track.nodes.length}`,
  );
  logLine(
    `Settings: chunkSizeMm=${settings.chunkSizeMm} supportCutClearanceMm=${settings.supportCutClearanceMm} separateSupport=${settings.separateSupport}`,
  );

  await reportProgress(onProgress, 0.02, 'Preparing meshes…');

  const rawPrims =
    opts?.supportPrimitives ??
    (opts?.supportsEnabled === false
      ? []
      : generateSupports(
          runtime,
          opts?.terrain ?? FLAT_TERRAIN,
          opts?.supportParams,
          opts?.supportOverrides ?? [],
        ).primitives);

  const sources = buildWholeMeshSources(runtime, rawPrims, trackShape);
  const supportAttachS = supportAttachArcLengths(runtime, rawPrims);
  logLine(
    `Supports: ${rawPrims.length} primitives, ${sources.hubs.length} hubs, ${sources.members.length} members, ${supportAttachS.length} track attaches`,
  );
  const boundaries = proposeTrackCuts(runtime, settings.chunkSizeMm, {
    supportAttachS,
    supportCutClearanceMm: settings.supportCutClearanceMm,
  });
  logLine(
    `Track cuts: ${boundaries.length - 2} interior (${boundaries.length - 1} chunks). Boundaries(m): ${boundaries.map((s) => s.toFixed(2)).join(', ')}`,
  );

  // Step 1 — sanity-union the whole coaster (track + supports) for early warnings.
  await reportProgress(onProgress, 0.08, 'Building whole solid…');
  const whole = await unionSweeps(sources.allMeshes);
  if (whole.warning) {
    warnings.push(`Whole solid: ${whole.warning}`);
    logLine(`WARN whole solid: ${whole.warning}`);
  }
  if (whole.solid) {
    try {
      whole.solid.delete();
    } catch {
      /* ignore */
    }
  }

  // Step 2 — remesh each contiguous arc interval. Infinite plane cuts of a
  // whole-track solid would also keep parallel return runs that happen to lie
  // on the same side of the plane — those are not connected along this chunk.
  await reportProgress(onProgress, 0.28, 'Cutting track into chunks…');
  const trackChunks = await cutTrackIntoContiguousChunks(
    runtime,
    boundaries,
    trackShape,
    onProgress,
    logLine,
  );

  // Step 3 — support pieces: hubs separate; members size-split mid-beam only.
  // Crossing beams get an auto junction sphere so chunking can split/connect there.
  await reportProgress(onProgress, 0.42, 'Splitting supports at hubs…');
  const footerPads = rawPrims
    .filter((p): p is Extract<TrackSupportPrimitive, { kind: 'footer' }> => p.kind === 'footer')
    .map((f) => ({ pos: f.pos.clone(), radius: f.radius, height: f.height }));
  const hubsWithCrossings = insertCrossingBeamHubs(sources.hubs, sources.members).filter(
    (h) => !jointOnFooterPad(h.pos, h.radius, footerPads),
  );
  const footers = detachFooters(rawPrims, runtime);
  const footerStations = footers
    .filter((f) => f.footerPos)
    .map((f) => ({ pos: f.footerPos!.clone(), radius: JOINT_PIN_RADIUS_M }));
  const supportPieces = detachSupportsAtHubs(
    hubsWithCrossings,
    sources.members,
    runtime,
    settings.chunkSizeMm,
    warnings,
    footerStations,
    footerPads,
  );
  const allSupport = [...supportPieces, ...footers];
  linkMemberEndsToHubs(allSupport);
  linkMemberEndsToFooters(allSupport);
  logLine(
    `Support pieces after hub detach: ${allSupport.length} (${supportPieces.length} split + ${footers.length} footers)`,
  );

  // Step 4 — stub + elbow (beams 1–2) always ride on their track chunk; optional
  // whole-tree absorb; remainder packs as support chunks. Never assign a stump
  // by spatial proximity alone — only by attach-S → contiguous chunk ownership.
  await reportProgress(onProgress, 0.48, 'Packing parts…');
  const onTrackByChunk: SupportPiece[][] = trackChunks.map(() => []);
  const overflowTrackSockets: { s: number; pin: TrackPinSpec }[] = [];

  const claimed = claimSeparateSupportTrackStumps(allSupport, settings.chunkSizeMm);
  let supportPool = claimed.remainder;
  const distributed = distributeConnectedOnTrack(
    claimed.onTrack,
    trackChunks,
    boundaries,
    runtime,
    settings.chunkSizeMm,
    warnings,
  );
  for (let ci = 0; ci < trackChunks.length; ci++) {
    onTrackByChunk[ci].push(...distributed[ci]);
  }
  supportPool.push(...distributed.leftover);
  markTrackCutSeeds(onTrackByChunk.flat(), supportPool);

  // Keep adding connected joint-parts onto each track chunk while they fit.
  supportPool = absorbWholeSupportsOntoTrack(
    onTrackByChunk,
    supportPool,
    trackChunks,
    settings.chunkSizeMm,
  );

  supportPool.push(
    ...refineOversizedTrackChunks(
      runtime,
      trackChunks,
      onTrackByChunk,
      trackShape,
      settings.chunkSizeMm,
      logLine,
    ),
  );
  markTrackCutSeeds(onTrackByChunk.flat(), supportPool);

  // Any leftover track pin (rare — claim normally keeps stubs on track) becomes
  // a male pin on the support pack + female socket on the owning track chunk.
  supportPool = supportPool.map((piece) => {
    if (!piece.trackPin) return piece;
    const pin = piece.trackPin;
    overflowTrackSockets.push({
      s: nearestS(runtime, pin.origin),
      pin: { origin: pin.origin.clone(), dir: pin.dir.clone() },
    });
    return withTrackPinApplied(piece);
  });
  logLine(
    `Packing: on-track pieces=[${onTrackByChunk.map((g) => g.length).join(',')}] ` +
      `supportPool=${supportPool.length} overflowTrackSockets=${overflowTrackSockets.length}`,
  );

  const supportGroups = groupSupportPieces(supportPool, settings.chunkSizeMm);
  logLine(`Support packs: ${supportGroups.length} groups (sizes ${supportGroups.map((g) => g.length).join(', ') || 'none'})`);

  const finalGroups: SupportPiece[][] = [
    ...onTrackByChunk.filter((g) => g.length > 0),
    ...supportGroups,
  ];
  // Always create male/female at every cut that crosses a pack boundary.
  applyJointsAcrossGroups(finalGroups);
  logTrackChunkSupportConnectors(onTrackByChunk, logLine);
  const jointAudit = auditSupportConnectors(finalGroups);
  for (const line of jointAudit.lines) logLine(line);
  for (const w of jointAudit.warnings) {
    warnings.push(w);
    logLine(`WARN ${w}`);
  }
  // Crossing beams in different packs stay solid otherwise — carve each out of the other.
  applyCrossPackIntersectionCuts(finalGroups);

  const parts: PrintPart[] = [];
  const nTrack = trackChunks.length;
  const nSupport = supportGroups.length;
  const nFix = Math.max(1, nTrack + nSupport);
  let trackIndex = 0;
  for (let ci = 0; ci < trackChunks.length; ci++) {
    const chunk = trackChunks[ci];
    const meshes: SweepMesh[] = [...chunk.meshes];
    const subtract: SweepMesh[] = [...chunk.subtract];

    for (const piece of onTrackByChunk[ci] ?? []) {
      meshes.push(piece.mesh);
      if (piece.additives) meshes.push(...piece.additives);
      if (piece.subtract) subtract.push(...piece.subtract);
    }
    if (!settings.separateSupport) {
      for (const sock of overflowTrackSockets) {
        if (!sInChunk(sock.s, chunk.s0, chunk.s1, ci === trackChunks.length - 1)) continue;
        addTrackSupportSocket(meshes, subtract, sock.pin);
      }
    }

    trackIndex += 1;
    const frac = 0.52 + (0.35 * (trackIndex - 1)) / nFix;
    await reportProgress(
      onProgress,
      frac,
      `Solidifying track ${trackIndex}/${nTrack}…`,
    );
    const fixedResult = await fixPrintSolid(meshes, { subtract });
    if (fixedResult.warning) {
      warnings.push(`Track ${pad(trackIndex)}: ${fixedResult.warning}`);
      logLine(`WARN Track ${pad(trackIndex)}: ${fixedResult.warning}`);
    }
    const mesh = fixedResult.mesh;
    const obb = pointsObbMm(meshPoints(mesh));
    const oversized = obbMaxSideMm(obb) > settings.chunkSizeMm + 1e-3;
    if (oversized) {
      const msg = `Track part ${pad(trackIndex)} OBB ${fmtObb(obb)} exceeds ${settings.chunkSizeMm} mm cube`;
      warnings.push(msg);
      logLine(`WARN ${msg}`);
    }
    logLine(
      `Part track-${pad(trackIndex)}: s=[${chunk.s0.toFixed(2)},${chunk.s1.toFixed(2)}] ` +
        `obb=${fmtObb(obb)} fixed=${fixedResult.fixed} oversized=${oversized} ` +
        `supportPieces=${(onTrackByChunk[ci] ?? []).length}`,
    );
    parts.push({
      id: `track-${pad(trackIndex)}`,
      kind: 'track',
      s0: chunk.s0,
      s1: chunk.s1,
      mesh,
      obbMm: obb,
      oversized,
      fixed: fixedResult.fixed,
      fixWarning: fixedResult.warning,
    });
  }

  let supportIndex = 0;
  for (const group of supportGroups) {
    supportIndex += 1;
    const meshes: SweepMesh[] = [];
    const subtract: SweepMesh[] = [];
    let s0 = Infinity;
    let s1 = -Infinity;
    for (const p of group) {
      meshes.push(p.mesh);
      if (p.additives) meshes.push(...p.additives);
      if (p.subtract) subtract.push(...p.subtract);
      s0 = Math.min(s0, p.s);
      s1 = Math.max(s1, p.s);
    }
    const frac = 0.52 + (0.35 * (nTrack + supportIndex - 1)) / nFix;
    await reportProgress(
      onProgress,
      frac,
      `Solidifying support ${supportIndex}/${nSupport}…`,
    );
    const fixedResult = await fixPrintSolid(meshes, { subtract });
    if (fixedResult.warning) {
      warnings.push(`Support ${pad(supportIndex)}: ${fixedResult.warning}`);
      logLine(`WARN Support ${pad(supportIndex)}: ${fixedResult.warning}`);
    }
    const mesh = fixedResult.mesh;
    const obb = pointsObbMm(meshPoints(mesh));
    const oversized = obbMaxSideMm(obb) > settings.chunkSizeMm + 1e-3;
    if (oversized) {
      const msg = `Support part ${pad(supportIndex)} OBB ${fmtObb(obb)} exceeds ${settings.chunkSizeMm} mm cube`;
      warnings.push(msg);
      logLine(`WARN ${msg}`);
    }
    logLine(
      `Part support-${pad(supportIndex)}: s≈[${Number.isFinite(s0) ? s0.toFixed(2) : '?'},${Number.isFinite(s1) ? s1.toFixed(2) : '?'}] ` +
        `obb=${fmtObb(obb)} fixed=${fixedResult.fixed} oversized=${oversized} pieces=${group.length}`,
    );
    parts.push({
      id: `support-${pad(supportIndex)}`,
      kind: 'support',
      s0: Number.isFinite(s0) ? s0 : 0,
      s1: Number.isFinite(s1) ? s1 : 0,
      mesh,
      obbMm: obb,
      oversized,
      fixed: fixedResult.fixed,
      fixWarning: fixedResult.warning,
    });
  }

  const atoms = collectPrintAtoms(trackChunks, onTrackByChunk, supportGroups);
  logLine(
    `Done: ${parts.length} chunks (${trackIndex} track, ${supportIndex} support), ` +
      `${atoms.length} split parts, ${warnings.length} warning(s)`,
  );
  await reportProgress(onProgress, 1, 'Done');
  const cuts = trackChunks.slice(1).map((c) => c.s0).filter((s) => s > 1e-6 && s < L - 1e-6);
  return { settings, cuts, parts, atoms, warnings, log };
}

/**
 * Build one mesh per contiguous arc interval [s0, s1].
 * Remeshes only that interval — never plane-cuts a whole-track solid, which
 * would also keep nearby parallel return runs that share a half-space.
 */
async function cutTrackIntoContiguousChunks(
  runtime: TrackRuntime,
  boundaries: number[],
  trackShape: TrackShapeSettings,
  onProgress?: PrintProgressCallback,
  logLine?: (line: string) => void,
): Promise<TrackChunkDraft[]> {
  const L = runtime.totalLength;
  const drafts: TrackChunkDraft[] = [];
  const intervals = boundaries.length - 1;
  const nChunks = Math.max(1, intervals);
  const cutFrac = (index: number) => 0.28 + (0.12 * index) / nChunks;
  // Closed circuits need a click-fit at the station seam whenever the loop is
  // split into more than one remeshed interval (pin on last chunk, sleeve on first).
  const closedWrap = runtime.closed && intervals > 1;

  for (let i = 0; i < intervals; i++) {
    const s0 = boundaries[i];
    const s1 = boundaries[i + 1];
    if (s1 - s0 < 1e-6) continue;
    await reportProgress(
      onProgress,
      cutFrac(i),
      `Cutting track into chunks… ${i + 1}/${nChunks}`,
    );
    const draft = trackChunkFromRemesh(runtime, s0, s1, L, trackShape, {
      closedWrapStart: closedWrap && s0 <= 1e-6,
      closedWrapEnd: closedWrap && s1 >= L - 1e-6,
      closeFullLoop: runtime.closed && intervals === 1 && s0 <= 1e-6 && s1 >= L - 1e-6,
    });
    drafts.push(draft);
    const connectors: string[] = [];
    if (runtime.closed && intervals === 1) {
      connectors.push('closedLoop');
    } else {
      if (s0 > 1e-6 || (closedWrap && s0 <= 1e-6)) connectors.push('female@start');
      if (s1 < L - 1e-6 || (closedWrap && s1 >= L - 1e-6)) connectors.push('male@end');
    }
    logLine?.(
      `Track chunk ${drafts.length}: s=[${s0.toFixed(2)},${s1.toFixed(2)}] connectors=[${connectors.join('+') || 'none'}]`,
    );
  }
  if (closedWrap) {
    logLine?.(
      'Closed-track wrap seam: male pin on last chunk @ s=L, female sleeve on first chunk @ s=0',
    );
  }
  return drafts;
}

function trackChunkFromRemesh(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  L: number,
  trackShape: TrackShapeSettings,
  opts?: {
    /** Female socket at s≈0 for the closed-circuit wrap. */
    closedWrapStart?: boolean;
    /** Male pin at s≈L for the closed-circuit wrap. */
    closedWrapEnd?: boolean;
    /** Whole closed circuit as one chunk — continuous loop, no seam joint. */
    closeFullLoop?: boolean;
  },
): TrackChunkDraft {
  const closeFullLoop = opts?.closeFullLoop === true;
  const meshes: SweepMesh[] = remeshTrackInterval(runtime, s0, s1, trackShape, {
    closeLoop: closeFullLoop,
  });
  const subtract: SweepMesh[] = [];
  if (closeFullLoop) {
    return { s0, s1, meshes, subtract };
  }
  // Track Type 2 keys the click-fit into its rectangular beam bar; Type 1 (and a
  // Type 2 with no beam) falls back to the round spine connector.
  const type2Geom = type2BeamJointGeom(trackShape);
  // Female at chunk start for interior cuts, or the closed wrap at s=0.
  if (s0 > 1e-6 || opts?.closedWrapStart) {
    const f0 = runtime.frameAt(s0);
    if (type2Geom) {
      addType2TrackFemale(meshes, subtract, f0, type2Geom);
    } else {
      meshes.push(trackSleeveMesh(f0));
      subtract.push(trackSleeveCavityMesh(f0));
    }
  }
  // Male at chunk end for interior cuts, or the closed wrap at s=L.
  if (s1 < L - 1e-6 || opts?.closedWrapEnd) {
    const f1 = runtime.frameAt(s1);
    meshes.push(type2Geom ? type2TrackPinMesh(f1, type2Geom) : trackPinMesh(f1));
  }
  return { s0, s1, meshes, subtract };
}

/** Centre of the Track Type 2 beam bar at a frame (below the rails, along +up). */
function type2BeamJointOrigin(f: Frame, geom: Type2BeamJointGeom): Vector3 {
  return f.railPos.clone().addScaledVector(f.up, geom.centerY);
}

/**
 * Male rectangular peg seated in the Type 2 beam bar, pointing +tan into the next
 * chunk. Cross-section matches the beam (inset by the socket wall + clearance).
 */
function type2TrackPinMesh(f: Frame, geom: Type2BeamJointGeom): SweepMesh {
  const ext = type2JointExtents(geom.halfW, geom.halfH);
  return boxPinMesh(
    type2BeamJointOrigin(f, geom),
    f.tan,
    f.right,
    f.up,
    ext.pegHalfW,
    ext.pegHalfH,
  );
}

/**
 * Female rectangular socket in the Type 2 beam bar: outer flush with the beam,
 * inner sized to the peg. The pin points +tan, so — like {@link trackSleeveMesh} —
 * the sleeve axis is flipped to −tan (shared right/up) so the collar sits inside
 * this chunk. Cavity is subtracted after union to keep the click-fit open.
 */
function addType2TrackFemale(
  meshes: SweepMesh[],
  subtract: SweepMesh[],
  f: Frame,
  geom: Type2BeamJointGeom,
): void {
  const origin = type2BeamJointOrigin(f, geom);
  const ext = type2JointExtents(geom.halfW, geom.halfH);
  const basis = jointBasis(origin, f.tan, f.right, f.up);
  const femaleDir = basis.dir.clone().multiplyScalar(-1);
  meshes.push(
    boxSleeveMesh(
      origin,
      femaleDir,
      basis.right,
      basis.up,
      ext.cavityHalfW,
      ext.cavityHalfH,
      ext.outerHalfW,
      ext.outerHalfH,
    ),
  );
  subtract.push(
    boxSleeveCavityMesh(origin, femaleDir, basis.right, basis.up, ext.cavityHalfW, ext.cavityHalfH),
  );
}

const PRINT_SAMPLE_STEP = 0.28;

function remeshTrackInterval(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  trackShape: TrackShapeSettings,
  opts?: { closeLoop?: boolean },
): SweepMesh[] {
  const len = Math.max(s1 - s0, 0.01);
  const n = Math.max(2, Math.ceil(len / PRINT_SAMPLE_STEP));
  const frames: Frame[] = [];
  for (let i = 0; i <= n; i++) frames.push(runtime.frameAt(s0 + (len * i) / n));
  const profile = printTrackProfile(trackShape);
  const { rails, spine } = trackLoopsForShape(trackShape, 'print', 14);
  const closeLoop = opts?.closeLoop === true;
  const pieces: SweepMesh[] = [
    ...rails.map((loop) => sweepProfile(frames, loop, { capEnds: !closeLoop, closeLoop })),
    ...spine.map((loop) => sweepProfile(frames, loop, { capEnds: !closeLoop, closeLoop })),
  ];
  pieces.push(...buildGearRackMeshes(runtime, s0, s1, trackShape, profile));
  return pieces;
}

/**
 * Detect colliding / crossing support members and insert junction spheres at the
 * hit so chunking can split mid-beam around the join (instead of overlapping solids).
 * Existing hubs are reused when the hit already sits on one.
 */
export function insertCrossingBeamHubs(
  hubs: HubSpec[],
  members: MemberSpec[],
): HubSpec[] {
  const out: HubSpec[] = hubs.map((h) => ({
    id: h.id,
    pos: h.pos.clone(),
    radius: h.radius,
  }));
  let seq = 0;

  const absorb = (pos: Vector3, radius: number) => {
    const existing = out.find((h) => h.pos.distanceTo(pos) < HUB_MATCH_M);
    if (existing) {
      existing.radius = Math.max(existing.radius, radius);
      return;
    }
    seq += 1;
    out.push({ id: `hub-cross-${seq}`, pos: pos.clone(), radius: Math.max(radius, 1e-4) });
  };

  for (let i = 0; i < members.length; i++) {
    for (let j = i + 1; j < members.length; j++) {
      const m1 = members[i];
      const m2 = members[j];
      if (membersShareEndpoint(m1, m2)) continue;

      const hit = segmentSegmentClosest(m1.a, m1.b, m2.a, m2.b);
      const r1 = m1.radiusA + (m1.radiusB - m1.radiusA) * hit.s;
      const r2 = m2.radiusA + (m2.radiusB - m2.radiusA) * hit.t;
      // Capsule overlap (small pad for numeric / mesh thickness).
      if (hit.dist > r1 + r2 + 0.06) continue;

      const nearEnd1 = hit.s < 0.04 || hit.s > 0.96;
      const nearEnd2 = hit.t < 0.04 || hit.t > 0.96;
      // Corner meet already has (or will get) endpoint hubs — skip.
      if (nearEnd1 && nearEnd2) continue;

      const pos = hit.c1.clone().lerp(hit.c2, 0.5);
      absorb(pos, Math.max(r1, r2));
    }
  }

  return out;
}

function membersShareEndpoint(a: MemberSpec, b: MemberSpec): boolean {
  return (
    a.a.distanceTo(b.a) < HUB_MATCH_M ||
    a.a.distanceTo(b.b) < HUB_MATCH_M ||
    a.b.distanceTo(b.a) < HUB_MATCH_M ||
    a.b.distanceTo(b.b) < HUB_MATCH_M
  );
}

/**
 * Split supports at hub spheres: each hub is its own piece; members are
 * half-split per joint span (then fit-split mid-beam if needed). Packing
 * re-bundles each joint with its half-beams into atomic parts.
 */
function detachSupportsAtHubs(
  hubs: HubSpec[],
  members: MemberSpec[],
  runtime: TrackRuntime,
  chunkSizeMm: number,
  warnings: string[],
  footerStations: { pos: Vector3; radius: number }[] = [],
  footerPads: { pos: Vector3; radius: number; height: number }[] = [],
): SupportPiece[] {
  const pieces: SupportPiece[] = [];
  const knownHubs: { pos: Vector3; radius: number }[] = [];

  for (const hub of hubs) {
    const mesh = solidJunctionMesh(hub.pos, hub.radius);
    if (obbMaxSideMm(pointsObbMm(meshPoints(mesh))) > chunkSizeMm + 1e-3) {
      warnings.push(
        `Support joint at (${hub.pos.x.toFixed(1)},${hub.pos.y.toFixed(1)},${hub.pos.z.toFixed(1)}) exceeds build cube`,
      );
    }
    pieces.push({
      mesh,
      points: meshPoints(mesh),
      s: nearestS(runtime, hub.pos),
      hubPos: hub.pos.clone(),
      hubRadius: hub.radius,
    });
    knownHubs.push({ pos: hub.pos.clone(), radius: hub.radius });
  }

  // Ground pads also divide members into spans (beam meets footer).
  for (const f of footerStations) {
    knownHubs.push({ pos: f.pos.clone(), radius: f.radius });
  }

  for (const mem of members) {
    const chain = splitCylinderChainWithJoints(
      mem.a,
      mem.b,
      mem.radiusA,
      mem.radiusB,
      chunkSizeMm,
      knownHubs,
      footerPads,
    );
    const beams = chain.filter((p) => !p.hubPos);
    if (mem.kind === 'column') {
      markTrackAttachIfNearRail(beams, mem.a, mem.b, runtime);
    } else if (mem.kind === 'stub') {
      markTrackAttach(beams, mem.a, mem.b);
    }
    const attachS = nearestS(runtime, mem.a.clone().lerp(mem.b, 0.5));
    for (const piece of chain) {
      pieces.push({ ...piece, s: attachS });
    }
  }

  return pieces;
}

function detachFooters(
  rawPrims: TrackSupportPrimitive[],
  runtime: TrackRuntime,
): SupportPiece[] {
  const prepared = prepareSupportPrimitives(rawPrims);
  const out: SupportPiece[] = [];
  for (const prim of prepared) {
    if (prim.kind !== 'footer') continue;
    const h = Math.max(prim.height, 0.12);
    // Junction is the pad top where the column sits (same as sphere joints).
    const top = new Vector3(prim.pos.x, prim.pos.y + h, prim.pos.z);
    const mesh = flatFooterMesh(prim.pos, prim.radius, prim.height);
    out.push({
      mesh,
      points: meshPoints(mesh),
      s: nearestS(runtime, prim.pos),
      footerPos: top,
    });
  }
  return out;
}

function hubKey(pos: Vector3): string {
  return `${pos.x.toFixed(3)},${pos.y.toFixed(3)},${pos.z.toFixed(3)}`;
}

/** Wire member endpoints (and column bodies near hubs) to joint spheres as male pin specs. */
export function linkMemberEndsToHubs(pieces: SupportPiece[]): void {
  const hubs = pieces.filter((p) => p.hubPos);
  for (const piece of pieces) {
    if (!piece.memberEnds || piece.memberEnds.length === 0) continue;
    const pins: HubPinSpec[] = [...(piece.hubPins ?? [])];
    for (const end of piece.memberEnds) {
      let best: SupportPiece | null = null;
      let bestDist = HUB_MATCH_M;
      for (const hub of hubs) {
        const d = hub.hubPos!.distanceTo(end.pos);
        if (d < bestDist) {
          bestDist = d;
          best = hub;
        }
      }
      if (!best?.hubPos) continue;
      const dir = end.along.clone().normalize();
      pins.push({
        origin: end.pos.clone(),
        dir,
        hubKey: hubKey(best.hubPos),
      });
    }
    if (pins.length > 0) piece.hubPins = pins;
  }

  for (const hub of hubs) {
    if (!hub.hubPos) continue;
    const key = hubKey(hub.hubPos);
    for (const piece of pieces) {
      if (!piece.memberEnds || piece.memberEnds.length < 2) continue;
      if ((piece.hubPins ?? []).some((p) => p.hubKey === key)) continue;
      const a = piece.memberEnds[0].pos;
      const b = piece.memberEnds[piece.memberEnds.length - 1].pos;
      const hit = closestPointOnSegment(hub.hubPos, a, b);
      if (hit.distanceTo(hub.hubPos) > HUB_MATCH_M) continue;
      const along = new Vector3().subVectors(b, a);
      if (along.lengthSq() < 1e-12) continue;
      along.normalize();
      const intoHub = new Vector3().subVectors(hub.hubPos, hit);
      if (intoHub.lengthSq() < 1e-12) {
        intoHub.copy(along);
      } else {
        intoHub.normalize();
      }
      piece.hubPins = [
        ...(piece.hubPins ?? []),
        { origin: hit.clone(), dir: intoHub, hubKey: key },
      ];
    }
  }
}

/** Wire member endpoints near ground pads — same role as hub spheres for chunking. */
export function linkMemberEndsToFooters(pieces: SupportPiece[]): void {
  const footers = pieces.filter((p) => p.footerPos);
  for (const piece of pieces) {
    if (!piece.memberEnds || piece.memberEnds.length === 0) continue;
    const pins: HubPinSpec[] = [...(piece.hubPins ?? [])];
    for (const end of piece.memberEnds) {
      let best: SupportPiece | null = null;
      let bestDist = FOOTER_MATCH_M;
      for (const foot of footers) {
        const d = horizontalDist(foot.footerPos!, end.pos);
        const dy = Math.abs(foot.footerPos!.y - end.pos.y);
        if (d < bestDist && dy < FOOTER_MATCH_M) {
          bestDist = d;
          best = foot;
        }
      }
      if (!best?.footerPos) continue;
      const key = hubKey(best.footerPos);
      if (pins.some((p) => p.hubKey === key)) continue;
      const dir = end.along.clone().normalize();
      pins.push({
        origin: end.pos.clone(),
        dir,
        hubKey: key,
      });
    }
    if (pins.length > 0) piece.hubPins = pins;
  }
}

function horizontalDist(a: Vector3, b: Vector3): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

function closestPointOnSegment(p: Vector3, a: Vector3, b: Vector3): Vector3 {
  const ab = new Vector3().subVectors(b, a);
  const lenSq = ab.lengthSq();
  if (lenSq < 1e-18) return a.clone();
  const t = Math.min(1, Math.max(0, new Vector3().subVectors(p, a).dot(ab) / lenSq));
  return a.clone().addScaledVector(ab, t);
}

export function applyCrossPackHubJoints(groupA: SupportPiece[], groupB: SupportPiece[]): void {
  connectHubs(groupA, groupB);
  connectHubs(groupB, groupA);
}

function connectHubs(hubGroup: SupportPiece[], memberGroup: SupportPiece[]): void {
  for (const member of memberGroup) {
    for (const pin of member.hubPins ?? []) {
      if (pin.applied) continue;
      const matchingHubs = hubGroup.filter((h) => {
        const pos = h.hubPos ?? h.footerPos;
        return pos != null && hubKey(pos) === pin.hubKey;
      });
      if (matchingHubs.length === 0) continue;
      // Footer pads are flat glue faces — never punch a pin/sleeve through them.
      if (matchingHubs.some((h) => h.footerPos)) continue;

      const beamR =
        member.beamRadius ??
        matchingHubs[0].hubRadius ??
        JOINT_PIN_RADIUS_M;
      const pinR = beamFitPinRadius(beamR);
      const keyFlat = beamFitKeyFlat(pinR);
      const pinSolid = pinMesh(pin.origin, pin.dir, undefined, undefined, pinR, keyFlat);
      member.additives = [...(member.additives ?? []), pinSolid];
      member.points = [...member.points, ...meshPoints(pinSolid)];

      // Pin dir points into the hub; sleeve convention extends along −dir, so flip
      // (and keep shared right/up) so the socket lives inside the hub sphere.
      const basis = jointBasis(pin.origin, pin.dir);
      const femaleDir = basis.dir.clone().multiplyScalar(-1);
      for (const hub of matchingHubs) {
        hub.additives = [
          ...(hub.additives ?? []),
          sleeveMesh(pin.origin, femaleDir, basis.right, basis.up, pinR, keyFlat),
        ];
        hub.subtract = [
          ...(hub.subtract ?? []),
          sleeveCavityMesh(pin.origin, femaleDir, basis.right, basis.up, pinR, keyFlat),
        ];
      }
      pin.applied = true;
    }
  }
}

export function applyCrossGroupChainJoints(groupA: SupportPiece[], groupB: SupportPiece[]): void {
  connectChainCuts(groupA, groupB);
  connectChainCuts(groupB, groupA);
}

/** Max distance between a mid-beam pin origin and its mating socket origin. */
const CHAIN_CUT_MATCH_M = 0.08;

type ChainPair = {
  male: SupportPiece;
  female: SupportPiece;
  dist: number;
  sameChain: boolean;
};

/**
 * Candidate female for a mid-beam pin. Socket origin must lie on the cut —
 * index alone is not enough (packing renumbers only one side of the split).
 */
function chainCutDistance(male: SupportPiece, female: SupportPiece): number | null {
  if (!male.chainPin || !female.chainSocket) return null;
  if (female.chainSocket.applied) return null;
  const dist = female.chainSocket.origin.distanceTo(male.chainPin.origin);
  if (dist > CHAIN_CUT_MATCH_M) return null;
  return dist;
}

function findChainSocketMate(
  male: SupportPiece,
  candidates: Iterable<SupportPiece>,
): SupportPiece | undefined {
  if (!male.chainPin) return undefined;

  let best: SupportPiece | undefined;
  let bestDist = Infinity;
  let bestSameChain = false;
  for (const p of candidates) {
    if (p === male) continue;
    const dist = chainCutDistance(male, p);
    if (dist == null) continue;
    const sameChain = male.chainId != null && p.chainId === male.chainId;
    // Prefer same-chain mates; among equals, closer origin wins.
    if (
      !best ||
      (sameChain && !bestSameChain) ||
      (sameChain === bestSameChain && dist < bestDist)
    ) {
      best = p;
      bestDist = dist;
      bestSameChain = sameChain;
    }
  }
  return best;
}

function applyChainJoint(male: SupportPiece, female: SupportPiece): void {
  if (!male.chainPin || !female.chainSocket) return;
  if (male.chainPin.applied || female.chainSocket.applied) return;
  const pin = male.chainPin;
  const sock = female.chainSocket;
  const pinR = pin.pinRadius;
  const keyFlat = beamFitKeyFlat(pinR);
  const pinSolid = pinMesh(pin.origin, pin.dir, undefined, undefined, pinR, keyFlat);
  male.additives = [...(male.additives ?? []), pinSolid];
  male.points = [...male.points, ...meshPoints(pinSolid)];
  // Pin/socket dir points into the female segment; sleeveMesh extends along −dir,
  // so flip (shared right/up) to keep the collar inside the female chunk.
  const basis = jointBasis(sock.origin, sock.dir);
  const femaleDir = basis.dir.clone().multiplyScalar(-1);
  female.additives = [
    ...(female.additives ?? []),
    sleeveMesh(sock.origin, femaleDir, basis.right, basis.up, pinR, keyFlat),
  ];
  female.subtract = [
    ...(female.subtract ?? []),
    sleeveCavityMesh(sock.origin, femaleDir, basis.right, basis.up, pinR, keyFlat),
  ];
  pin.applied = true;
  sock.applied = true;
}

function connectChainCuts(maleGroup: SupportPiece[], femaleGroup: SupportPiece[]): void {
  // 1:1 assignment so a socket cannot be claimed by two pins.
  const pairs: ChainPair[] = [];
  for (const male of maleGroup) {
    if (!male.chainPin || male.chainPin.applied) continue;
    for (const female of femaleGroup) {
      if (female === male) continue;
      const dist = chainCutDistance(male, female);
      if (dist == null) continue;
      pairs.push({
        male,
        female,
        dist,
        sameChain: male.chainId != null && female.chainId === male.chainId,
      });
    }
  }
  pairs.sort((a, b) => {
    if (a.sameChain !== b.sameChain) return a.sameChain ? -1 : 1;
    return a.dist - b.dist;
  });
  for (const pair of pairs) {
    if (pair.male.chainPin?.applied || pair.female.chainSocket?.applied) continue;
    applyChainJoint(pair.male, pair.female);
  }
}

export function applyJointsAcrossGroups(groups: SupportPiece[][]): void {
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      applyCrossPackHubJoints(groups[i], groups[j]);
    }
  }
  // Mid-beam cuts: 1:1 origin match across the full piece set, but only emit
  // pin/sleeve when the two halves landed in different packs.
  const pieceGroup = new Map<SupportPiece, number>();
  for (let gi = 0; gi < groups.length; gi++) {
    for (const p of groups[gi]) pieceGroup.set(p, gi);
  }
  const all = groups.flat();
  const pairs: ChainPair[] = [];
  for (const male of all) {
    if (!male.chainPin || male.chainPin.applied) continue;
    for (const female of all) {
      if (female === male) continue;
      if (pieceGroup.get(male) === pieceGroup.get(female)) continue;
      const dist = chainCutDistance(male, female);
      if (dist == null) continue;
      pairs.push({
        male,
        female,
        dist,
        sameChain: male.chainId != null && female.chainId === male.chainId,
      });
    }
  }
  pairs.sort((a, b) => {
    if (a.sameChain !== b.sameChain) return a.sameChain ? -1 : 1;
    return a.dist - b.dist;
  });
  for (const pair of pairs) {
    if (pair.male.chainPin?.applied || pair.female.chainSocket?.applied) continue;
    applyChainJoint(pair.male, pair.female);
  }
}

/** Per track chunk (same numbering as track-NNN parts): applied support click-fits. */
function logTrackChunkSupportConnectors(
  onTrackByChunk: SupportPiece[][],
  logLine: (line: string) => void,
): void {
  for (let ti = 0; ti < onTrackByChunk.length; ti++) {
    const joints: string[] = [];
    for (const piece of onTrackByChunk[ti] ?? []) {
      if (piece.chainPin?.applied) {
        const o = piece.chainPin.origin;
        joints.push(
          `male@(${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) chain=${piece.chainId}#${piece.chainIndex}`,
        );
      }
      if (piece.chainSocket?.applied) {
        const o = piece.chainSocket.origin;
        joints.push(
          `female@(${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) chain=${piece.chainId}#${piece.chainIndex}`,
        );
      }
      for (const hp of piece.hubPins ?? []) {
        if (!hp.applied) continue;
        const o = hp.origin;
        joints.push(`hubPin@(${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) key=${hp.hubKey}`);
      }
      if (piece.hubPos && (piece.additives?.length ?? 0) > 0) {
        const o = piece.hubPos;
        joints.push(
          `hubSleeve@(${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) n=${piece.additives!.length}`,
        );
      }
    }
    if (joints.length > 0) {
      logLine(`track-${pad(ti + 1)} support connectors: ${joints.join('; ')}`);
    }
  }
}

/**
 * Summarise which support connectors were applied across pack boundaries.
 * Unapplied pins whose mate lives in another pack are real failures; same-pack
 * leftovers stay fused by union and are expected.
 */
export function auditSupportConnectors(groups: SupportPiece[][]): {
  lines: string[];
  warnings: string[];
} {
  const lines: string[] = [];
  const warnings: string[] = [];
  const pieceGroup = new Map<SupportPiece, number>();
  for (let gi = 0; gi < groups.length; gi++) {
    for (const p of groups[gi]) pieceGroup.set(p, gi);
  }

  let hubApplied = 0;
  let hubSamePack = 0;
  let hubOrphan = 0;
  let chainApplied = 0;
  let chainSamePack = 0;
  let chainOrphan = 0;

  for (let gi = 0; gi < groups.length; gi++) {
    for (const member of groups[gi]) {
      for (const pin of member.hubPins ?? []) {
        if (pin.applied) {
          hubApplied += 1;
          // A pin/sleeve into a joint means the pack boundary landed on that
          // joint instead of halfway along a beam.
          const o = pin.origin;
          warnings.push(
            `Pack boundary falls on a joint at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) ` +
              `— beam and joint print as separate parts`,
          );
          continue;
        }
        const mateSame = groups[gi].some((h) => {
          const pos = h.hubPos ?? h.footerPos;
          return pos != null && hubKey(pos) === pin.hubKey;
        });
        const mateGi = groups.findIndex(
          (g, idx) =>
            idx !== gi &&
            g.some((h) => {
              const pos = h.hubPos ?? h.footerPos;
              return pos != null && hubKey(pos) === pin.hubKey;
            }),
        );
        if (mateGi >= 0) {
          hubOrphan += 1;
          const o = pin.origin;
          warnings.push(
            `Hub connector missing between packs ${gi}↔${mateGi} at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) key=${pin.hubKey}`,
          );
        } else if (mateSame) {
          hubSamePack += 1;
        } else {
          hubOrphan += 1;
          const o = pin.origin;
          warnings.push(
            `Hub connector mate missing for pin at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)}) key=${pin.hubKey}`,
          );
        }
      }

      if (member.chainPin) {
        if (member.chainPin.applied) {
          chainApplied += 1;
          continue;
        }
        const female = findChainSocketMate(member, pieceGroup.keys());
        if (female && pieceGroup.get(female) !== gi) {
          chainOrphan += 1;
          const o = member.chainPin.origin;
          warnings.push(
            `Mid-beam connector missing on chain ${member.chainId}#${member.chainIndex}→${female.chainIndex} ` +
              `packs ${gi}↔${pieceGroup.get(female)} at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)})`,
          );
        } else if (!female) {
          chainOrphan += 1;
          const o = member.chainPin.origin;
          warnings.push(
            `Mid-beam connector mate missing for chain ${member.chainId}#${member.chainIndex} ` +
              `at (${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)})`,
          );
        } else {
          chainSamePack += 1;
        }
      }
    }
  }

  lines.push(
    `Support connectors: hub applied=${hubApplied} samePack=${hubSamePack} orphan=${hubOrphan}; ` +
      `mid-beam applied=${chainApplied} samePack=${chainSamePack} orphan=${chainOrphan}`,
  );
  return { lines, warnings };
}

/**
 * When support pieces in different print packs occupy the same space (crossing
 * braces, etc.), subtract each solid from the other so both print with a mating
 * cut instead of two overlapping solids.
 *
 * Skips pairs already joined by hub or mid-member connectors.
 */
export function applyCrossPackIntersectionCuts(groups: SupportPiece[][]): void {
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      for (const a of groups[i]) {
        for (const b of groups[j]) {
          if (skipCrossPackIntersectionCut(a, b)) continue;
          if (!pointsAabbOverlap(a.points, b.points)) continue;
          a.subtract = [...(a.subtract ?? []), b.mesh];
          b.subtract = [...(b.subtract ?? []), a.mesh];
        }
      }
    }
  }
}

/** True when A and B already share a hub pin/sleeve or an adjacent chain cut. */
function skipCrossPackIntersectionCut(a: SupportPiece, b: SupportPiece): boolean {
  if (a.mesh.indices.length < 3 || b.mesh.indices.length < 3) return true;
  if (hubConnected(a, b) || hubConnected(b, a)) return true;
  if (chainAdjacent(a, b) || chainCutConnected(a, b)) return true;
  // Crossing beams that meet at the same auto/elbow sphere use hub connectors.
  if (shareHubKey(a, b)) return true;
  // Pieces that already carry a click-fit pin/sleeve must not be boolean-carved
  // by unrelated struts in the other pack — that eats the connector.
  if (hasAppliedClickFit(a) || hasAppliedClickFit(b)) return true;
  return false;
}

function hasAppliedClickFit(p: SupportPiece): boolean {
  if (p.chainPin?.applied || p.chainSocket?.applied) return true;
  if ((p.hubPins ?? []).some((h) => h.applied)) return true;
  return false;
}

/** Mid-beam halves that meet at the same cut origin — even if chainIndex desynced. */
function chainCutConnected(a: SupportPiece, b: SupportPiece): boolean {
  if (a.chainPin && b.chainSocket) {
    if (a.chainPin.origin.distanceTo(b.chainSocket.origin) <= CHAIN_CUT_MATCH_M) return true;
  }
  if (b.chainPin && a.chainSocket) {
    if (b.chainPin.origin.distanceTo(a.chainSocket.origin) <= CHAIN_CUT_MATCH_M) return true;
  }
  return false;
}

function shareHubKey(a: SupportPiece, b: SupportPiece): boolean {
  const keys = new Set((a.hubPins ?? []).map((p) => p.hubKey));
  if ((b.hubPins ?? []).some((p) => keys.has(p.hubKey))) return true;
  // Also treat member ends that meet within hub match as the same junction.
  for (const ea of a.memberEnds ?? []) {
    for (const eb of b.memberEnds ?? []) {
      if (ea.pos.distanceTo(eb.pos) <= HUB_MATCH_M) return true;
    }
  }
  return false;
}

function hubConnected(hubSide: SupportPiece, memberSide: SupportPiece): boolean {
  const pos = hubSide.hubPos ?? hubSide.footerPos;
  if (!pos) return false;
  const key = hubKey(pos);
  return (memberSide.hubPins ?? []).some((p) => p.hubKey === key);
}

function chainAdjacent(a: SupportPiece, b: SupportPiece): boolean {
  if (!a.chainId || a.chainId !== b.chainId) return false;
  if (a.chainIndex == null || b.chainIndex == null) return false;
  return Math.abs(a.chainIndex - b.chainIndex) === 1;
}

function pointsAabbOverlap(a: Vector3[], b: Vector3[], pad = 0.05): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const aa = pointsAabb(a);
  const bb = pointsAabb(b);
  return (
    aa.minX - pad <= bb.maxX + pad &&
    aa.maxX + pad >= bb.minX - pad &&
    aa.minY - pad <= bb.maxY + pad &&
    aa.maxY + pad >= bb.minY - pad &&
    aa.minZ - pad <= bb.maxZ + pad &&
    aa.maxZ + pad >= bb.minZ - pad
  );
}

function pointsAabb(points: Vector3[]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
} {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, maxX, minY, maxY, minZ, maxZ };
}

function withTrackPinApplied(piece: SupportPiece): SupportPiece {
  if (!piece.trackPin) return piece;
  const pin = pinMesh(piece.trackPin.origin, piece.trackPin.dir);
  return {
    ...piece,
    additives: [...(piece.additives ?? []), pin],
    points: [...piece.points, ...meshPoints(pin)],
    trackPin: undefined,
  };
}

/**
 * Female socket on the track for a support pin.
 * Pin dir points from support into the track; the sleeve/cavity must use the
 * opposite axis so the hole lives inside the track and overlaps the pin.
 * Shared right/up keeps the D-key aligned after the dir flip.
 */
export function addTrackSupportSocket(
  meshes: SweepMesh[],
  subtract: SweepMesh[],
  pin: TrackPinSpec,
): void {
  const basis = jointBasis(pin.origin, pin.dir);
  const femaleDir = basis.dir.clone().multiplyScalar(-1);
  meshes.push(sleeveMesh(pin.origin, femaleDir, basis.right, basis.up));
  subtract.push(sleeveCavityMesh(pin.origin, femaleDir, basis.right, basis.up));
}

function markTrackAttachIfNearRail(
  chain: Omit<SupportPiece, 's'>[],
  a: Vector3,
  b: Vector3,
  runtime: TrackRuntime,
): void {
  const s = nearestS(runtime, a);
  if (runtime.frameAt(s).railPos.distanceTo(a) > TRACK_ATTACH_MAX_M) return;
  markTrackAttach(chain, a, b);
}

function markTrackAttach(chain: Omit<SupportPiece, 's'>[], a: Vector3, b: Vector3): void {
  if (chain.length === 0) return;
  const dir = new Vector3().subVectors(a, b);
  if (dir.lengthSq() < 1e-12) return;
  dir.normalize();
  // Mutate in place — callers may pass a filtered view of the real chain array.
  chain[0].trackPin = { origin: a.clone(), dir };
}

/**
 * Slice a member into beam segments. Existing hubs on the member mark span
 * endpoints (beams meet spheres) but are never used as size-split points.
 * Each joint↔joint span is first cut exactly halfway so each joint owns a half;
 * halves that still exceed the cube are recursively mid-split for fit.
 */
export function splitCylinderChainWithJoints(
  a: Vector3,
  b: Vector3,
  radiusA: number,
  radiusB: number,
  chunkSizeMm: number,
  existingHubs: { pos: Vector3; radius: number }[] = [],
  footerPads: { pos: Vector3; radius: number; height: number }[] = [],
): Omit<SupportPiece, 's'>[] {
  const len = Math.max(a.distanceTo(b), 1e-6);
  const ab = new Vector3().subVectors(b, a);
  const dir = ab.clone().multiplyScalar(1 / len);

  // Structural hubs only — they divide the member into spans; no fit spheres.
  type Station = { t: number; pos: Vector3 };
  const hubTs: Station[] = [];
  const addHubT = (t: number, pos: Vector3) => {
    if (t < 0.02 || t > 0.98) return;
    if (hubTs.some((s) => Math.abs(s.t - t) < 0.015)) return;
    hubTs.push({ t, pos: pos.clone() });
  };

  for (const h of existingHubs) {
    const hit = closestPointOnSegment(h.pos, a, b);
    if (hit.distanceTo(h.pos) > HUB_MATCH_M) continue;
    const t = new Vector3().subVectors(hit, a).dot(dir) / len;
    addHubT(t, hit);
  }
  hubTs.sort((x, y) => x.t - y.t);

  const stations: Station[] = [
    { t: 0, pos: a.clone() },
    ...hubTs,
    { t: 1, pos: b.clone() },
  ];

  const chainId = nextChainId(a, b);
  const out: Omit<SupportPiece, 's'>[] = [];
  let chainIndex = 0;

  for (let i = 0; i < stations.length - 1; i++) {
    const t0 = stations[i].t;
    const t1 = stations[i + 1].t;
    const p0 = stations[i].pos;
    const p1 = stations[i + 1].pos;
    const r0 = radiusA + (radiusB - radiusA) * t0;
    const r1 = radiusA + (radiusB - radiusA) * t1;
    if (p0.distanceTo(p1) < 1e-4) continue;

    // Always half each joint span first (each joint owns the near half).
    let segs: CylSeg[];
    const spanLen = p0.distanceTo(p1);
    if (beamCanBeHalved(spanLen)) {
      const mid = p0.clone().lerp(p1, 0.5);
      const rMid = (r0 + r1) * 0.5;
      segs = [
        ...splitCylinderSegments(p0, mid, r0, rMid, chunkSizeMm),
        ...splitCylinderSegments(mid, p1, rMid, r1, chunkSizeMm),
      ];
    } else {
      segs = splitCylinderSegments(p0, p1, r0, r1, chunkSizeMm);
    }

    for (let s = 0; s < segs.length; s++) {
      const seg = segs[s];
      const capA = endOnFooterPad(seg.a, footerPads);
      const capB = endOnFooterPad(seg.b, footerPads);
      const mesh = cylinderMesh(seg.a, seg.b, seg.radiusA, seg.radiusB, {
        horizontalCapA: capA,
        horizontalCapB: capB,
      });
      const beamR = (seg.radiusA + seg.radiusB) * 0.5;
      const pinR = beamFitPinRadius(beamR);
      const along = new Vector3().subVectors(seg.b, seg.a);
      const piece: Omit<SupportPiece, 's'> = {
        mesh,
        points: meshPoints(mesh),
        chainId,
        chainIndex,
        beamRadius: beamR,
        footerCapEnd: [capA, capB],
      };
      if (along.lengthSq() >= 1e-12) {
        along.normalize();
        piece.memberEnds = [
          { pos: seg.a.clone(), along: along.clone().multiplyScalar(-1) },
          { pos: seg.b.clone(), along: along.clone() },
        ];
      }

      // Mid-beam connectors between size-split segments (never at hubs).
      if (s < segs.length - 1) {
        piece.chainPin = {
          origin: seg.b.clone(),
          dir: along.clone(),
          pinRadius: pinR,
        };
      }
      if (s > 0) {
        const prevR = (segs[s - 1].radiusA + segs[s - 1].radiusB) * 0.5;
        piece.chainSocket = {
          origin: seg.a.clone(),
          dir: along.clone(),
          pinRadius: beamFitPinRadius(prevR),
        };
      }

      out.push(piece);
      chainIndex += 1;
    }
  }

  return out;
}

let chainSeq = 0;
function nextChainId(a: Vector3, b: Vector3): string {
  chainSeq += 1;
  return `c${chainSeq}:${a.x.toFixed(2)},${a.y.toFixed(2)},${a.z.toFixed(2)}>${b.x.toFixed(2)},${b.y.toFixed(2)},${b.z.toFixed(2)}`;
}

interface CylSeg {
  a: Vector3;
  b: Vector3;
  radiusA: number;
  radiusB: number;
}

/** Leave room for a mid-beam pin/sleeve so a flush cube fit does not overflow after joints. */
function chunkFitLimitMm(chunkSizeMm: number): number {
  return printChunkFitLimitMm(chunkSizeMm);
}

/**
 * Size-split a cylinder by recursively cutting at the geometric midpoint until
 * each piece fits the build cube. Packing consumes these whole halves as-is.
 */
function splitCylinderSegments(
  a: Vector3,
  b: Vector3,
  radiusA: number,
  radiusB: number,
  chunkSizeMm: number,
  depth = 0,
): CylSeg[] {
  const full = cylinderMesh(a, b, radiusA, radiusB);
  if (obbMaxSideMm(pointsObbMm(meshPoints(full))) <= chunkFitLimitMm(chunkSizeMm) + 1e-3) {
    return [{ a: a.clone(), b: b.clone(), radiusA, radiusB }];
  }

  const len = Math.max(a.distanceTo(b), 1e-6);
  // Guard against pathological oversize (sphere/radius larger than cube).
  if (depth > 24 || len < 1e-3) {
    return [{ a: a.clone(), b: b.clone(), radiusA, radiusB }];
  }

  const mid = a.clone().lerp(b, 0.5);
  const rMid = (radiusA + radiusB) * 0.5;
  const left = splitCylinderSegments(a, mid, radiusA, rMid, chunkSizeMm, depth + 1);
  const right = splitCylinderSegments(mid, b, rMid, radiusB, chunkSizeMm, depth + 1);
  return [...left, ...right];
}

/** Geometry-only slices (no joints) — used by tests. */
export function splitCylinderToFit(
  a: Vector3,
  b: Vector3,
  radiusA: number,
  radiusB: number,
  chunkSizeMm: number,
): SweepMesh[] {
  return splitCylinderSegments(a, b, radiusA, radiusB, chunkSizeMm).map((s) =>
    cylinderMesh(s.a, s.b, s.radiusA, s.radiusB),
  );
}

function nearestS(runtime: TrackRuntime, p: Vector3): number {
  const L = runtime.totalLength;
  const step = Math.max(0.5, L / 400);
  let bestS = 0;
  let bestD = Infinity;
  for (let s = 0; s <= L; s += step) {
    const d = runtime.frameAt(s).railPos.distanceToSquared(p);
    if (d < bestD) {
      bestD = d;
      bestS = s;
    }
  }
  const lo = Math.max(0, bestS - step);
  const hi = Math.min(L, bestS + step);
  for (let s = lo; s <= hi; s += step / 10) {
    const d = runtime.frameAt(s).railPos.distanceToSquared(p);
    if (d < bestD) {
      bestD = d;
      bestS = s;
    }
  }
  return bestS;
}

/** Arc-length of every stub↔rail connection (beam 1 track end). */
function supportAttachArcLengths(
  runtime: TrackRuntime,
  prims: TrackSupportPrimitive[],
): number[] {
  const out: number[] = [];
  for (const prim of prims) {
    if (prim.kind === 'stub') {
      out.push(nearestS(runtime, prim.a));
    } else if (prim.kind === 'column') {
      // Straight columns without a stub still pin near the rail at end `a`.
      const s = nearestS(runtime, prim.a);
      if (runtime.frameAt(s).railPos.distanceTo(prim.a) <= TRACK_ATTACH_MAX_M) {
        out.push(s);
      }
    }
  }
  return out;
}

function chunkIndexForS(s: number, boundaries: number[]): number {
  for (let i = 0; i < boundaries.length - 1; i++) {
    const s0 = boundaries[i];
    const s1 = boundaries[i + 1];
    if (s >= s0 && (s < s1 || (i === boundaries.length - 2 && s <= s1 + 1e-6))) return i;
  }
  return -1;
}

function sInChunk(s: number, s0: number, s1: number, isLast: boolean): boolean {
  return s >= s0 && (s < s1 || (isLast && s <= s1 + 1e-6));
}

/**
 * Pack support pieces into build-volume groups.
 *
 * Beams 3/4/6/7/8 are pre-bundled into joint-parts (each hub/footer + all of its
 * half-beams). A footer always keeps at least one half-beam. Chunks are filled
 * by adding whole parts:
 * - Seed from track-cut remainders (end of beam 2 / elbow stump) first.
 * - Prefer parts that climb upward (+Y) when growing.
 * - Keep adding connected parts until nothing else fits.
 * - Next chunk again prefers unassigned track-cut parts, then leftovers.
 */
export function groupSupportPieces(
  items: SupportPiece[],
  chunkSizeMm: number,
): SupportPiece[][] {
  if (items.length === 0) return [];

  const parts = buildJointParts(items, chunkSizeMm);
  const groups = groupSupportParts(parts, chunkSizeMm);
  return groups.map((g) => g.flatMap((p) => p.pieces));
}

/** Stamp remainders that share a mid-beam cut with a track-owned stump. */
export function markTrackCutSeeds(onTrack: SupportPiece[], remainder: SupportPiece[]): void {
  for (const r of remainder) {
    r.fromTrackCut = false;
    for (const t of onTrack) {
      if (
        r.chainId &&
        r.chainId === t.chainId &&
        r.chainIndex != null &&
        t.chainIndex != null &&
        Math.abs(r.chainIndex - t.chainIndex) === 1
      ) {
        r.fromTrackCut = true;
        break;
      }
    }
  }
}

function isJunctionPiece(p: SupportPiece): boolean {
  return !!(p.hubPos || p.footerPos);
}

function partBeams(part: SupportPart): SupportPiece[] {
  return part.pieces.filter(
    (p) => !isJunctionPiece(p) && !!p.memberEnds && p.memberEnds.length >= 2,
  );
}

function junctionsCoincide(a: SupportPiece, b: SupportPiece): boolean {
  if (a === b) return true;
  const ap = a.hubPos ?? a.footerPos;
  const bp = b.hubPos ?? b.footerPos;
  if (!ap || !bp) return false;
  if (a.footerPos || b.footerPos) {
    return (
      horizontalDist(ap, bp) <= FOOTER_MATCH_M && Math.abs(ap.y - bp.y) <= FOOTER_MATCH_M
    );
  }
  return ap.distanceTo(bp) <= HUB_MATCH_M;
}

function uniqueTouchingJunctions(
  beam: SupportPiece,
  junctions: SupportPiece[],
): SupportPiece[] {
  const touching = junctions.filter((j) => beamTouchesJunction(beam, j));
  const unique: SupportPiece[] = [];
  for (const j of touching) {
    if (unique.some((u) => junctionsCoincide(u, j))) continue;
    unique.push(j);
  }
  return unique;
}

function junctionEndIndex(beam: SupportPiece, junction: SupportPiece): number {
  const pos = junction.hubPos ?? junction.footerPos;
  if (!pos || !beam.memberEnds) return -1;
  return beam.memberEnds.findIndex((e) => {
    if (junction.footerPos) {
      return (
        horizontalDist(pos, e.pos) <= FOOTER_MATCH_M &&
        Math.abs(pos.y - e.pos.y) <= FOOTER_MATCH_M
      );
    }
    return e.pos.distanceTo(pos) <= HUB_MATCH_M;
  });
}

function beamTouchesJunction(beam: SupportPiece, junction: SupportPiece): boolean {
  return junctionEndIndex(beam, junction) >= 0;
}

/** Joint spheres / ground pads sitting at `pos`. */
function junctionPiecesAt(pieces: Iterable<SupportPiece>, pos: Vector3): SupportPiece[] {
  const out: SupportPiece[] = [];
  for (const p of pieces) {
    if (p.hubPos) {
      if (p.hubPos.distanceTo(pos) <= HUB_MATCH_M) out.push(p);
    } else if (p.footerPos) {
      if (
        horizontalDist(p.footerPos, pos) <= FOOTER_MATCH_M &&
        Math.abs(p.footerPos.y - pos.y) <= FOOTER_MATCH_M
      ) {
        out.push(p);
      }
    }
  }
  return out;
}

function partPoints(part: SupportPart): Vector3[] {
  return part.pieces.flatMap((p) => p.points);
}

function partFitsLimit(points: Vector3[], chunkSizeMm: number): boolean {
  return obbMaxSideMm(pointsObbMm(points)) <= chunkFitLimitMm(chunkSizeMm) + 1e-3;
}

function trackChunkPoints(chunk: TrackChunkDraft, pieces: SupportPiece[]): Vector3[] {
  return [...chunk.meshes.flatMap((m) => meshPoints(m)), ...pieces.flatMap((p) => p.points)];
}

function trackChunkFits(chunk: TrackChunkDraft, pieces: SupportPiece[], chunkSizeMm: number): boolean {
  return obbMaxSideMm(pointsObbMm(trackChunkPoints(chunk, pieces))) <= chunkSizeMm + 1e-3;
}

/**
 * Mid-split outgoing beam stumps on the track chunk until it fits the cube.
 * Never moves a whole beam off a joint — the pack boundary stays mid-beam.
 * Stub + elbow always stay on the track.
 */
function peelTrackChunkToFit(
  chunk: TrackChunkDraft,
  pieces: SupportPiece[],
  chunkSizeMm: number,
): SupportPiece[] {
  const leftover: SupportPiece[] = [];
  const allPieces = () => [...pieces, ...leftover];
  let guard = 0;
  while (!trackChunkFits(chunk, pieces, chunkSizeMm) && guard++ < 32) {
    let bestIdx = -1;
    let bestLen = -1;
    for (let i = 0; i < pieces.length; i++) {
      const p = pieces[i];
      if (p.trackPin || isJunctionPiece(p) || !p.memberEnds || p.memberEnds.length < 2) continue;
      const len = p.memberEnds[0].pos.distanceTo(p.memberEnds[1].pos);
      if (!beamCanBeHalved(len)) continue;
      if (len > bestLen) {
        bestLen = len;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) break;
    const beam = pieces[bestIdx];
    const fromEnd = peelNearEndIndex(beam, pieces);
    const split = midSplitBeamPiece(beam, fromEnd, 0.5, allPieces());
    if (!split) break;
    transferTrackCutFlag(beam, split.stump, split.remainder);
    pieces[bestIdx] = split.stump;
    leftover.push(split.remainder);
  }
  const all = allPieces();
  linkMemberEndsToHubs(all);
  linkMemberEndsToFooters(all);
  return leftover;
}

/** End of `beam` that still meets a junction on this chunk (else the track-pin end). */
function peelNearEndIndex(beam: SupportPiece, pieces: SupportPiece[]): number {
  if (!beam.memberEnds || beam.memberEnds.length < 2) return 0;
  for (const j of pieces) {
    if (j === beam || !isJunctionPiece(j)) continue;
    const endIdx = junctionEndIndex(beam, j);
    if (endIdx >= 0) return endIdx;
  }
  if (beam.trackPin) {
    return beam.memberEnds[0].pos.distanceTo(beam.trackPin.origin) <=
      beam.memberEnds[1].pos.distanceTo(beam.trackPin.origin)
      ? 0
      : 1;
  }
  return 0;
}

/**
 * If a track chunk is still over the cube after peeling optional stumps, split
 * it at the midpoint (keeping support clearance) and remesh both sides.
 */
function refineOversizedTrackChunks(
  runtime: TrackRuntime,
  trackChunks: TrackChunkDraft[],
  onTrackByChunk: SupportPiece[][],
  trackShape: TrackShapeSettings,
  chunkSizeMm: number,
  logLine: (line: string) => void,
): SupportPiece[] {
  const L = runtime.totalLength;
  const minArcM = minTrackChunkArcM(chunkSizeMm);
  const leftover: SupportPiece[] = [];
  let guard = 0;
  while (guard++ < 64) {
    let split = false;
    for (let i = 0; i < trackChunks.length; i++) {
      const chunk = trackChunks[i];
      leftover.push(...peelTrackChunkToFit(chunk, onTrackByChunk[i], chunkSizeMm));
      if (trackChunkFits(chunk, onTrackByChunk[i], chunkSizeMm)) continue;
      if (chunk.s1 - chunk.s0 < 2 * minArcM - 1e-9) continue;

      const mid = (chunk.s0 + chunk.s1) / 2;
      const left = trackChunkFromRemesh(runtime, chunk.s0, mid, L, trackShape, {
        closedWrapStart: runtime.closed && chunk.s0 <= 1e-6,
        closedWrapEnd: false,
      });
      const right = trackChunkFromRemesh(runtime, mid, chunk.s1, L, trackShape, {
        closedWrapStart: false,
        closedWrapEnd: runtime.closed && chunk.s1 >= L - 1e-6,
      });
      const leftPieces: SupportPiece[] = [];
      const rightPieces: SupportPiece[] = [];
      for (const p of onTrackByChunk[i]) {
        if (p.s < mid) leftPieces.push(p);
        else rightPieces.push(p);
      }
      leftover.push(...peelTrackChunkToFit(left, leftPieces, chunkSizeMm));
      leftover.push(...peelTrackChunkToFit(right, rightPieces, chunkSizeMm));
      trackChunks.splice(i, 1, left, right);
      onTrackByChunk.splice(i, 1, leftPieces, rightPieces);
      logLine(
        `Split oversized track chunk at s=${mid.toFixed(2)} → ` +
          `[${chunk.s0.toFixed(2)},${mid.toFixed(2)}] + [${mid.toFixed(2)},${chunk.s1.toFixed(2)}]`,
      );
      split = true;
      break;
    }
    if (!split) break;
  }
  return leftover;
}

function partsAdjacent(a: SupportPart, b: SupportPart): boolean {
  for (const pa of a.pieces) {
    for (const pb of b.pieces) {
      if (piecesAdjacent(pa, pb)) return true;
    }
  }
  return false;
}

function partConnected(part: SupportPart, group: SupportPart[]): boolean {
  return group.some((g) => partsAdjacent(part, g));
}

function isTrackSeedPart(part: SupportPart): boolean {
  return part.pieces.some((p) => !!(p.trackPin || p.fromTrackCut));
}

function partAscent(part: SupportPart, group: SupportPart[]): number {
  const groupPieces = group.flatMap((p) => p.pieces);
  let best = -Infinity;
  for (const piece of part.pieces) {
    best = Math.max(best, beamAscentFromGroup(piece, groupPieces));
  }
  if (!Number.isFinite(best)) return 0;
  return best;
}

function partSortKey(part: SupportPart): number {
  return part.pieces.reduce((s, p) => Math.min(s, p.s), Infinity);
}

/**
 * Bundle each hub/footer with every half-beam that meets it. Beams that still
 * span two junctions are half-split first. Oversized parts are further
 * mid-split so each pack atom fits the cube alone. Ground footers always keep
 * at least one half-beam — never a bare pad.
 */
export function buildJointParts(items: SupportPiece[], chunkSizeMm: number): SupportPart[] {
  const pool: SupportPiece[] = [...items];

  const splitMultiHubBeams = () => {
    let guard = 0;
    while (guard++ < 64) {
      const junctions = pool.filter(isJunctionPiece);
      let splitOne = false;
      for (const beam of [...pool]) {
        if (isJunctionPiece(beam) || !beam.memberEnds || beam.memberEnds.length < 2) continue;
        const touching = uniqueTouchingJunctions(beam, junctions);
        if (touching.length < 2) continue;
        // Too short to halve — both joints stay in one part instead of cutting
        // a stub so close to a joint that it cannot be connected.
        if (!beamCanBeHalved(beam.memberEnds[0].pos.distanceTo(beam.memberEnds[1].pos))) {
          continue;
        }

        // Prefer splitting from the higher junction so upward ownership stays clear.
        touching.sort((a, b) => {
          const ya = (a.hubPos ?? a.footerPos)!.y;
          const yb = (b.hubPos ?? b.footerPos)!.y;
          return yb - ya;
        });
        const from = touching[0];
        const endIdx = junctionEndIndex(beam, from);
        if (endIdx < 0) continue;
        const split = midSplitBeamPiece(beam, endIdx, 0.5, pool);
        if (!split) continue;
        const idx = pool.indexOf(beam);
        if (idx >= 0) pool.splice(idx, 1, split.stump, split.remainder);
        else pool.push(split.stump, split.remainder);
        splitOne = true;
        break;
      }
      if (!splitOne) break;
    }
    linkMemberEndsToHubs(pool);
    linkMemberEndsToFooters(pool);
  };

  splitMultiHubBeams();

  const used = new Set<SupportPiece>();
  const parts: SupportPart[] = [];
  const claimBeams = (junctions: SupportPiece[]): SupportPiece[] => {
    const beams: SupportPiece[] = [];
    for (const beam of pool) {
      if (used.has(beam) || isJunctionPiece(beam)) continue;
      if (junctions.some((j) => beamTouchesJunction(beam, j))) {
        beams.push(beam);
        used.add(beam);
      }
    }
    return beams;
  };

  // Joints that cannot be separated travel as one part: co-located pad/sphere
  // pairs, and any two joints bridged by a beam too short to halve.
  const junctions = pool.filter(isJunctionPiece);
  const parentOf = new Map<SupportPiece, SupportPiece>();
  const find = (p: SupportPiece): SupportPiece => {
    let root = p;
    while ((parentOf.get(root) ?? root) !== root) root = parentOf.get(root)!;
    parentOf.set(p, root);
    return root;
  };
  const union = (a: SupportPiece, b: SupportPiece): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parentOf.set(rb, ra);
  };
  for (const j of junctions) parentOf.set(j, j);
  for (let i = 0; i < junctions.length; i++) {
    for (let k = i + 1; k < junctions.length; k++) {
      if (junctionsCoincide(junctions[i], junctions[k])) union(junctions[i], junctions[k]);
    }
  }
  for (const beam of pool) {
    if (isJunctionPiece(beam)) continue;
    const touching = uniqueTouchingJunctions(beam, junctions);
    for (let i = 1; i < touching.length; i++) union(touching[0], touching[i]);
  }

  const byRoot = new Map<SupportPiece, SupportPiece[]>();
  for (const j of junctions) {
    const root = find(j);
    const list = byRoot.get(root);
    if (list) list.push(j);
    else byRoot.set(root, [j]);
  }

  // A group holding a ground pad keeps the pad as its junction; a hub sphere
  // co-located with it is consumed and its ball mesh dropped so the pad
  // underside stays planar.
  for (const members of byRoot.values()) {
    const footer = members.find((m) => m.footerPos) ?? null;
    const pieces: SupportPiece[] = [];
    for (const j of members) {
      used.add(j);
      if (footer && j !== footer && j.hubPos && junctionsCoincide(footer, j)) continue;
      pieces.push(j);
    }
    pieces.push(...claimBeams(members));
    parts.push({ pieces, junction: footer ?? members[0] });
  }

  for (const beam of pool) {
    if (used.has(beam)) continue;
    parts.push({ pieces: [beam], junction: null });
    used.add(beam);
  }

  attachHalfBeamsToBareFooters(parts);
  return shrinkJointPartsToFit(
    parts.filter((p) => p.pieces.length > 0),
    chunkSizeMm,
  );
}

/** Steal a half-beam for any footer that ended up as a bare pad. */
function attachHalfBeamsToBareFooters(parts: SupportPart[]): void {
  const allPieces = () => parts.flatMap((p) => p.pieces);
  for (const part of parts) {
    const footer = part.junction;
    if (!footer?.footerPos) continue;
    if (partBeams(part).length > 0) continue;

    for (const other of parts) {
      if (other === part) continue;
      const beam = partBeams(other).find((b) => beamTouchesJunction(b, footer));
      if (!beam) continue;
      const endIdx = junctionEndIndex(beam, footer);
      if (endIdx < 0) continue;
      const len = beam.memberEnds![0].pos.distanceTo(beam.memberEnds![1].pos);
      const split = beamCanBeHalved(len)
        ? midSplitBeamPiece(beam, endIdx, 0.5, allPieces())
        : null;
      if (split) {
        other.pieces = other.pieces.map((p) => (p === beam ? split.remainder : p));
        part.pieces.push(split.stump);
        break;
      }
      // Too short to halve: merge the two parts rather than move a beam whose
      // far end sits on the other joint — a boundary must never fall on a joint.
      part.pieces.push(...other.pieces);
      other.pieces = [];
      break;
    }
  }
}

/**
 * If a joint-part alone exceeds the cube, keep mid-splitting its beams from the
 * junction until the near stump fits; far remainders become their own parts.
 */
function shrinkJointPartsToFit(partsIn: SupportPart[], chunkSizeMm: number): SupportPart[] {
  const parts: SupportPart[] = partsIn.map((p) => ({
    pieces: [...p.pieces],
    junction: p.junction,
  }));
  const allPieces = () => parts.flatMap((p) => p.pieces);

  let guard = 0;
  while (guard++ < 128) {
    let grew = false;
    for (let pi = 0; pi < parts.length; pi++) {
      const part = parts[pi];
      if (partFitsLimit(partPoints(part), chunkSizeMm)) continue;

      const junction = part.junction;
      if (!junction) {
        // Orphan beam chain segment — half it and fork a new part.
        const beam = part.pieces.find((p) => !isJunctionPiece(p) && p.memberEnds);
        if (!beam?.memberEnds || beam.memberEnds.length < 2) continue;
        const len = beam.memberEnds[0].pos.distanceTo(beam.memberEnds[1].pos);
        if (!beamCanBeHalved(len)) continue;
        const split = midSplitBeamPiece(beam, 0, 0.5, allPieces());
        if (!split) continue;
        part.pieces = part.pieces.map((p) => (p === beam ? split.stump : p));
        transferTrackCutFlag(beam, split.stump, split.remainder);
        parts.push({ pieces: [split.remainder], junction: null });
        grew = true;
        break;
      }

      // Longest beam attached to the junction. Footers always keep at least one
      // half-beam — further splits of that last stump are still allowed to fit.
      const beams = part.pieces.filter(
        (p) => p !== junction && !isJunctionPiece(p) && p.memberEnds && p.memberEnds.length >= 2,
      );
      beams.sort((a, b) => {
        const la = a.memberEnds![0].pos.distanceTo(a.memberEnds![1].pos);
        const lb = b.memberEnds![0].pos.distanceTo(b.memberEnds![1].pos);
        return lb - la;
      });

      let splitDone = false;
      for (const beam of beams) {
        const endIdx = junctionEndIndex(beam, junction);
        if (endIdx < 0) continue;
        const a = beam.memberEnds![endIdx].pos;
        const b = beam.memberEnds![1 - endIdx].pos;
        if (!beamCanBeHalved(a.distanceTo(b))) continue;
        // Far end on another joint of this merged part — forking it off would put
        // the pack boundary on that joint.
        if (junctionPiecesAt(part.pieces, b).length > 0) continue;
        const split = midSplitBeamPiece(beam, endIdx, 0.5, allPieces());
        if (!split) continue;
        part.pieces = part.pieces.map((p) => (p === beam ? split.stump : p));
        transferTrackCutFlag(beam, split.stump, split.remainder);
        parts.push({ pieces: [split.remainder], junction: null });
        splitDone = true;
        grew = true;
        break;
      }
      if (splitDone) break;
    }
    if (!grew) break;
  }

  // Re-link pins after any splits.
  const all = allPieces();
  linkMemberEndsToHubs(all);
  linkMemberEndsToFooters(all);
  return parts;
}

function groupSupportParts(parts: SupportPart[], chunkSizeMm: number): SupportPart[][] {
  if (parts.length === 0) return [];

  const assigned = new Set<SupportPart>();
  const groups: SupportPart[][] = [];
  const continueFrom: SupportPart[] = [];

  const fits = (group: SupportPart[], extra: SupportPart): boolean => {
    const pts = group.flatMap(partPoints).concat(partPoints(extra));
    return partFitsLimit(pts, chunkSizeMm);
  };

  const trackSeeds = () =>
    parts
      .filter((p) => !assigned.has(p) && isTrackSeedPart(p))
      .sort((a, b) => {
        const sa = partSortKey(a) - partSortKey(b);
        if (Math.abs(sa) > 1e-9) return sa;
        return partAscent(b, []) - partAscent(a, []);
      });

  const leftoverSeeds = () =>
    parts
      .filter((p) => !assigned.has(p))
      .sort((a, b) => {
        const ua = partAscent(a, []);
        const ub = partAscent(b, []);
        if (Math.abs(ub - ua) > 1e-6) return ub - ua;
        const sa = partSortKey(a) - partSortKey(b);
        if (Math.abs(sa) > 1e-9) return sa;
        const ja = a.junction ? 0 : 1;
        const jb = b.junction ? 0 : 1;
        if (ja !== jb) return ja - jb;
        return 0;
      });

  const growCluster = (seed: SupportPart): SupportPart[] => {
    const group: SupportPart[] = [];
    const groupSet = new Set<SupportPart>();

    const tryAdd = (part: SupportPart): boolean => {
      if (assigned.has(part) || groupSet.has(part)) return false;
      if (group.length > 0 && !fits(group, part)) return false;
      if (group.length > 0 && !partConnected(part, group)) return false;
      group.push(part);
      groupSet.add(part);
      assigned.add(part);
      return true;
    };

    // Seed alone may exceed the cube (pathological) — still place it.
    if (!tryAdd(seed)) {
      group.push(seed);
      groupSet.add(seed);
      assigned.add(seed);
    }

    let grew = true;
    while (grew) {
      grew = false;
      const candidates = parts.filter(
        (p) => !assigned.has(p) && !groupSet.has(p) && partConnected(p, group),
      );
      candidates.sort((a, b) => {
        const ua = partAscent(a, group);
        const ub = partAscent(b, group);
        if (Math.abs(ub - ua) > 1e-6) return ub - ua;
        const ta = isTrackSeedPart(a) ? 0 : 1;
        const tb = isTrackSeedPart(b) ? 0 : 1;
        if (ta !== tb) return ta - tb;
        return partSortKey(a) - partSortKey(b);
      });
      for (const cand of candidates) {
        if (tryAdd(cand)) grew = true;
      }
    }

    return group;
  };

  const queueContinueFrom = (group: SupportPart[]) => {
    const pending: SupportPart[] = [];
    const seen = new Set<SupportPart>(continueFrom);
    for (const p of parts) {
      if (assigned.has(p) || seen.has(p)) continue;
      if (!partConnected(p, group)) continue;
      seen.add(p);
      pending.push(p);
    }
    pending.sort((a, b) => {
      const ua = partAscent(a, group);
      const ub = partAscent(b, group);
      if (Math.abs(ub - ua) > 1e-6) return ub - ua;
      return partSortKey(a) - partSortKey(b);
    });
    for (const p of pending) {
      if (!continueFrom.includes(p)) continueFrom.push(p);
    }
  };

  const pickContinueSeed = (): SupportPart | null => {
    while (continueFrom.length > 0) {
      const next = continueFrom.shift()!;
      if (!assigned.has(next)) return next;
    }
    return null;
  };

  while (true) {
    let seed: SupportPart | null = null;

    // Prefer unassigned parts hanging off track (end of beam 2) for every new chunk.
    const track = trackSeeds()[0];
    if (track) seed = track;

    if (!seed) {
      seed = pickContinueSeed();
    }

    if (!seed) {
      seed = leftoverSeeds()[0] ?? null;
    }

    if (!seed) break;

    const group = growCluster(seed);
    if (group.length === 0) {
      assigned.add(seed);
      continue;
    }
    queueContinueFrom(group);
    groups.push(group);
  }

  // Seed order can leave two connected packs that still fit in one cube.
  // Join those so leftover parts are not stranded beside an undersized chunk.
  let merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        if (!groups[j].some((p) => partConnected(p, groups[i]))) continue;
        const pts = groups[i].flatMap(partPoints).concat(groups[j].flatMap(partPoints));
        if (!partFitsLimit(pts, chunkSizeMm)) continue;
        groups[i] = groups[i].concat(groups[j]);
        groups.splice(j, 1);
        merged = true;
        break outer;
      }
    }
  }

  return groups;
}

/** True when `piece` shares a hub or mid-beam chain edge with any of `others`. */
function piecesConnected(piece: SupportPiece, others: SupportPiece[]): boolean {
  for (const o of others) {
    if (piecesAdjacent(piece, o)) return true;
  }
  return false;
}

/** Connected components under {@link piecesAdjacent}. */
function supportConnectedComponents(pieces: SupportPiece[]): SupportPiece[][] {
  const remaining = new Set(pieces);
  const comps: SupportPiece[][] = [];
  while (remaining.size > 0) {
    const start = remaining.values().next().value!;
    remaining.delete(start);
    const comp: SupportPiece[] = [start];
    const stack = [start];
    while (stack.length > 0) {
      const cur = stack.pop()!;
      for (const q of [...remaining]) {
        if (!piecesAdjacent(cur, q)) continue;
        remaining.delete(q);
        comp.push(q);
        stack.push(q);
      }
    }
    comps.push(comp);
  }
  return comps;
}

/**
 * Assign claimed track-stump pieces (stub + elbow + halves) to the track chunk
 * their attach pin maps to, in arc-S order. Stumps always stay on track (fit is
 * only warned). Optional whole-tree absorb further along the chunk stops at the
 * first tree that does not fit — see {@link absorbWholeSupportsOntoTrack}.
 */
function distributeConnectedOnTrack(
  onTrack: SupportPiece[],
  trackChunks: TrackChunkDraft[],
  boundaries: number[],
  runtime: TrackRuntime,
  chunkSizeMm: number,
  warnings: string[],
): SupportPiece[][] & { leftover: SupportPiece[] } {
  const nChunks = trackChunks.length;
  const byChunk: SupportPiece[][] & { leftover: SupportPiece[] } = Object.assign(
    Array.from({ length: nChunks }, () => [] as SupportPiece[]),
    { leftover: [] as SupportPiece[] },
  );
  if (onTrack.length === 0) return byChunk;

  type Cluster = { pieces: SupportPiece[]; attachS: number };
  const pending: Cluster[][] = Array.from({ length: nChunks }, () => []);

  for (const comp of supportConnectedComponents(onTrack)) {
    const pinned = comp.find((p) => p.trackPin);
    const attachS = pinned?.trackPin
      ? nearestS(runtime, pinned.trackPin.origin)
      : comp[0].s;
    let ci = chunkIndexForS(attachS, boundaries);
    if (ci < 0 && nChunks > 0) {
      ci = Math.min(
        nChunks - 1,
        Math.max(
          0,
          boundaries.findIndex((b, i) => i < boundaries.length - 1 && attachS < boundaries[i + 1]),
        ),
      );
      if (ci < 0) ci = 0;
    }
    if (ci < 0) {
      warnings.push('Track-attach support stump could not be mapped to a track chunk');
      byChunk.leftover.push(...comp);
      continue;
    }
    for (const p of comp) p.s = attachS;
    pending[ci].push({ pieces: comp, attachS });
  }

  for (let ci = 0; ci < nChunks; ci++) {
    // Arc order along the chunk — never by spatial “what else fits in the box”.
    const ordered = pending[ci].sort((a, b) => a.attachS - b.attachS);
    for (const cluster of ordered) {
      byChunk[ci].push(...cluster.pieces);
    }
    if (byChunk[ci].length === 0) continue;
    const peeled = peelTrackChunkToFit(trackChunks[ci], byChunk[ci], chunkSizeMm);
    byChunk.leftover.push(...peeled);
    if (peeled.length > 0) {
      warnings.push(
        `Track chunk ${pad(ci + 1)} overflowed the cube — moved ${peeled.length} support stump(s) to a support pack`,
      );
    }
  }
  return byChunk;
}

/**
 * Absorb remainder joint-parts onto track chunks while they still fit.
 * Grows from the on-track stub/elbow one connected part at a time so a small
 * track chunk keeps filling instead of leaving the whole leftover tree as a
 * separate support pack. Parts that also hang off a different track chunk are
 * skipped (those stay mid-beam support packs).
 */
export function absorbWholeSupportsOntoTrack(
  onTrackByChunk: SupportPiece[][],
  poolIn: SupportPiece[],
  trackChunks: TrackChunkDraft[],
  chunkSizeMm: number,
): SupportPiece[] {
  if (poolIn.length === 0) return poolIn;

  const parts = buildJointParts(poolIn, chunkSizeMm);
  const assigned = new Set<SupportPart>();

  const touchesChunk = (part: SupportPart, ci: number): boolean =>
    part.pieces.some((p) => piecesConnected(p, onTrackByChunk[ci]));

  const hangsOffOtherChunk = (part: SupportPart, ci: number): boolean => {
    for (let i = 0; i < onTrackByChunk.length; i++) {
      if (i === ci || onTrackByChunk[i].length === 0) continue;
      if (part.pieces.some((p) => piecesConnected(p, onTrackByChunk[i]))) return true;
    }
    return false;
  };

  for (let ci = 0; ci < onTrackByChunk.length; ci++) {
    if (onTrackByChunk[ci].length === 0) continue;
    const trackAsGroup: SupportPart[] = [{ pieces: onTrackByChunk[ci], junction: null }];

    let grew = true;
    while (grew) {
      grew = false;
      const candidates = parts.filter(
        (p) => !assigned.has(p) && touchesChunk(p, ci) && !hangsOffOtherChunk(p, ci),
      );
      candidates.sort((a, b) => {
        const ua = partAscent(a, trackAsGroup);
        const ub = partAscent(b, trackAsGroup);
        if (Math.abs(ub - ua) > 1e-6) return ub - ua;
        return partSortKey(a) - partSortKey(b);
      });
      for (const cand of candidates) {
        if (!trackChunkFits(trackChunks[ci], [...onTrackByChunk[ci], ...cand.pieces], chunkSizeMm)) {
          continue;
        }
        const attachS =
          onTrackByChunk[ci].find((p) => p.trackPin)?.s ??
          onTrackByChunk[ci][0]?.s ??
          cand.pieces.reduce((s, p) => Math.min(s, p.s), Infinity);
        for (const p of cand.pieces) {
          p.s = attachS;
          p.fromTrackCut = false;
        }
        onTrackByChunk[ci].push(...cand.pieces);
        assigned.add(cand);
        grew = true;
        break;
      }
    }
  }

  return parts.filter((p) => !assigned.has(p)).flatMap((p) => p.pieces);
}

/**
 * How strongly a beam climbs away from the current cluster (+Y preferred).
 * Hubs score 0; used to prioritize upward beams when packing support chunks.
 */
function beamAscentFromGroup(piece: SupportPiece, group: SupportPiece[]): number {
  if (piece.hubPos || piece.footerPos) return 0;
  const ends = piece.memberEnds;
  if (!ends || ends.length < 2) {
    if (piece.points.length < 2) return 0;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const p of piece.points) {
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
    return maxY - minY;
  }

  const touchesGroup = (pos: Vector3): boolean => {
    for (const g of group) {
      if (g.hubPos && g.hubPos.distanceTo(pos) <= HUB_MATCH_M) return true;
      if (
        g.footerPos &&
        horizontalDist(g.footerPos, pos) <= FOOTER_MATCH_M &&
        Math.abs(g.footerPos.y - pos.y) <= FOOTER_MATCH_M
      ) {
        return true;
      }
      for (const ge of g.memberEnds ?? []) {
        if (ge.pos.distanceTo(pos) <= HUB_MATCH_M) return true;
      }
    }
    return false;
  };

  // Ascent = far.y - near.y for the end that attaches to the cluster.
  if (touchesGroup(ends[0].pos) && !touchesGroup(ends[1].pos)) {
    return ends[1].pos.y - ends[0].pos.y;
  }
  if (touchesGroup(ends[1].pos) && !touchesGroup(ends[0].pos)) {
    return ends[0].pos.y - ends[1].pos.y;
  }
  if (touchesGroup(ends[0].pos) && touchesGroup(ends[1].pos)) {
    // Both ends touch (short span): prefer the orientation with larger +Y span.
    return Math.abs(ends[1].pos.y - ends[0].pos.y);
  }

  // No attach to group yet (seed / leftover): prefer beams that continue upward
  // from their incoming cut (chainSocket) or simply climb in +Y.
  if (piece.chainSocket) {
    const cut = piece.chainSocket.origin;
    const far =
      ends[0].pos.distanceTo(cut) <= ends[1].pos.distanceTo(cut) ? ends[1] : ends[0];
    return far.pos.y - cut.y;
  }
  return ends[1].pos.y - ends[0].pos.y;
}

function piecesAdjacent(a: SupportPiece, b: SupportPiece): boolean {
  if (chainAdjacent(a, b) || chainCutConnected(a, b)) return true;
  for (const ea of a.memberEnds ?? []) {
    for (const eb of b.memberEnds ?? []) {
      if (ea.pos.distanceTo(eb.pos) <= CHAIN_CUT_MATCH_M) return true;
    }
  }
  const aPos = a.hubPos ?? a.footerPos;
  const bPos = b.hubPos ?? b.footerPos;
  if (aPos && (b.hubPins ?? []).some((p) => p.hubKey === hubKey(aPos))) return true;
  if (bPos && (a.hubPins ?? []).some((p) => p.hubKey === hubKey(bPos))) return true;
  if (a.hubPos && b.memberEnds) {
    if (b.memberEnds.some((e) => e.pos.distanceTo(a.hubPos!) <= HUB_MATCH_M)) return true;
  }
  if (b.hubPos && a.memberEnds) {
    if (a.memberEnds.some((e) => e.pos.distanceTo(b.hubPos!) <= HUB_MATCH_M)) return true;
  }
  if (a.footerPos && b.memberEnds) {
    if (
      b.memberEnds.some(
        (e) =>
          horizontalDist(a.footerPos!, e.pos) <= FOOTER_MATCH_M &&
          Math.abs(a.footerPos!.y - e.pos.y) <= FOOTER_MATCH_M,
      )
    ) {
      return true;
    }
  }
  if (b.footerPos && a.memberEnds) {
    if (
      a.memberEnds.some(
        (e) =>
          horizontalDist(b.footerPos!, e.pos) <= FOOTER_MATCH_M &&
          Math.abs(b.footerPos!.y - e.pos.y) <= FOOTER_MATCH_M,
      )
    ) {
      return true;
    }
  }
  return false;
}

/**
 * SeparateSupport / track ownership: for each track attachment, keep the
 * perpendicular stub and the elbow sphere (beams 1–2 from Beams.md), plus the
 * already-halved outgoing beam stumps owned by the elbow. Remainder joint-parts
 * start support packing from that mid-beam cut.
 */
export function claimSeparateSupportTrackStumps(
  pieces: SupportPiece[],
  chunkSizeMm: number,
): { onTrack: SupportPiece[]; remainder: SupportPiece[] } {
  const pool = [...pieces];
  const onTrackSet = new Set<SupportPiece>();
  void chunkSizeMm; // API kept for callers; track stumps are structural halves.

  const hubs = () => pool.filter((p) => p.hubPos);
  const trackSeeds = pool.filter((p) => p.trackPin);

  const junctionsAt = (pos: Vector3): SupportPiece[] => junctionPiecesAt(pool, pos);

  // Junctions claimed onto the track whose outgoing beams still need halving.
  const pendingJunctions: SupportPiece[] = [];
  const claimJunction = (j: SupportPiece): void => {
    if (onTrackSet.has(j)) return;
    onTrackSet.add(j);
    pendingJunctions.push(j);
  };

  /**
   * Claim a beam leaving an already-claimed junction (or the rail) at `nearEndIdx`.
   * Long enough → keep the near half so the pack boundary is the mid-beam cut.
   * Too short to halve → keep it whole and claim the far junction as well, so the
   * boundary moves past the joint instead of landing on it.
   */
  const claimBeamFromEnd = (beam: SupportPiece, nearEndIdx: number): void => {
    if (onTrackSet.has(beam)) return;
    const ends = beam.memberEnds;
    if (!ends || ends.length < 2) {
      onTrackSet.add(beam);
      return;
    }
    const farJunctions = junctionsAt(ends[1 - nearEndIdx].pos);
    if (farJunctions.length === 0) {
      // Far end is already a mid-beam cut — this whole half rides on the track.
      onTrackSet.add(beam);
      return;
    }
    if (beamCanBeHalved(ends[0].pos.distanceTo(ends[1].pos))) {
      const split = midSplitBeamPiece(beam, nearEndIdx, 0.5, pool);
      if (split) {
        const idx = pool.indexOf(beam);
        if (idx >= 0) pool.splice(idx, 1, split.stump, split.remainder);
        else pool.push(split.stump, split.remainder);
        onTrackSet.add(split.stump);
        return;
      }
    }
    onTrackSet.add(beam);
    for (const j of farJunctions) claimJunction(j);
  };

  /** Halve (or absorb) every beam hanging off the junctions claimed so far. */
  const drainJunctions = (): void => {
    let guard = 0;
    while (pendingJunctions.length > 0 && guard++ < 512) {
      const junction = pendingJunctions.shift()!;
      for (const beam of [...pool]) {
        if (isJunctionPiece(beam) || onTrackSet.has(beam)) continue;
        const endIdx = junctionEndIndex(beam, junction);
        if (endIdx < 0) continue;
        claimBeamFromEnd(beam, endIdx);
      }
    }
  };

  for (const stub of [...trackSeeds]) {
    if (onTrackSet.has(stub)) continue;
    // May have been replaced by a mid-beam stump while claiming another attach.
    if (!pool.includes(stub)) continue;

    // Elbow / attach hubs touching the stub.
    const stubHubs: SupportPiece[] = [];
    for (const hub of hubs()) {
      if (!hub.hubPos) continue;
      const touches =
        (stub.hubPins ?? []).some((p) => p.hubKey === hubKey(hub.hubPos!)) ||
        (stub.memberEnds ?? []).some((e) => e.pos.distanceTo(hub.hubPos!) <= HUB_MATCH_M);
      if (touches) stubHubs.push(hub);
    }

    if (stubHubs.length === 0) {
      // Straight column with no elbow — keep a halfway stump of the track beam.
      const ends = stub.memberEnds;
      if (!ends || ends.length < 2) {
        onTrackSet.add(stub);
        continue;
      }
      // Track attach is at the trackPin.origin end.
      const fromEnd = stub.trackPin
        ? ends[0].pos.distanceTo(stub.trackPin.origin) <=
          ends[1].pos.distanceTo(stub.trackPin.origin)
          ? 0
          : 1
        : 0;
      claimBeamFromEnd(stub, fromEnd);
      drainJunctions();
      continue;
    }

    // Stub + elbow (beams 1–2) always ride on the track chunk.
    onTrackSet.add(stub);
    for (const hub of stubHubs) claimJunction(hub);
    drainJunctions();
  }

  // Re-link after splits so hub/footer pins land on the correct beam pieces.
  linkMemberEndsToHubs(pool);
  linkMemberEndsToFooters(pool);

  // Short columns often stay wholly on the track chunk. Keep their ground pad
  // with that column — never leave a bare footer in the support remainder.
  for (const foot of pool) {
    if (!foot.footerPos || onTrackSet.has(foot)) continue;
    let remBeam = false;
    let trackBeam = false;
    for (const p of pool) {
      if (p === foot || isJunctionPiece(p)) continue;
      if (!beamTouchesJunction(p, foot)) continue;
      if (onTrackSet.has(p)) trackBeam = true;
      else remBeam = true;
    }
    if (remBeam) continue;
    if (trackBeam) {
      onTrackSet.add(foot);
      continue;
    }
    if (pool.some((p) => onTrackSet.has(p) && piecesAdjacent(p, foot))) {
      onTrackSet.add(foot);
    }
  }

  // Stamp every on-track piece with the stub's rail arc-length so elbow/stump
  // fragments map to the same track chunk as the attachment.
  for (const stub of pool) {
    if (!stub.trackPin || !onTrackSet.has(stub)) continue;
    const attachS = stub.s;
    const cluster: SupportPiece[] = [];
    const seen = new Set<SupportPiece>();
    const stack = [stub];
    while (stack.length > 0) {
      const cur = stack.pop()!;
      if (seen.has(cur) || !onTrackSet.has(cur)) continue;
      seen.add(cur);
      cluster.push(cur);
      for (const q of pool) {
        if (!seen.has(q) && onTrackSet.has(q) && piecesAdjacent(cur, q)) stack.push(q);
      }
    }
    for (const p of cluster) p.s = attachS;
  }

  const onTrack: SupportPiece[] = [];
  const remainder: SupportPiece[] = [];
  for (const p of pool) {
    if (onTrackSet.has(p)) onTrack.push(p);
    else remainder.push(p);
  }
  return { onTrack, remainder };
}

/**
 * Keep {@link SupportPiece.fromTrackCut} on the half that still occupies the
 * original track-side cut, not blindly on `remainder`.
 */
function transferTrackCutFlag(
  src: SupportPiece,
  stump: SupportPiece,
  remainder: SupportPiece,
): void {
  if (!src.fromTrackCut) return;
  src.fromTrackCut = false;
  const inherit = (child: SupportPiece): boolean => {
    if (
      src.chainSocket &&
      child.chainSocket &&
      src.chainSocket.origin.distanceTo(child.chainSocket.origin) <= CHAIN_CUT_MATCH_M
    ) {
      return true;
    }
    if (
      src.chainPin &&
      child.chainPin &&
      src.chainPin.origin.distanceTo(child.chainPin.origin) <= CHAIN_CUT_MATCH_M
    ) {
      return true;
    }
    return false;
  };
  if (inherit(stump) && !inherit(remainder)) stump.fromTrackCut = true;
  else remainder.fromTrackCut = true;
}

/**
 * Cut `beam` at fraction `t` along the axis starting from memberEnds[fromEnd].
 * Stump keeps the near (fromEnd) side; remainder the far side.
 * Chain indices increase along memberEnds[0]→[1]; connectors always mid-beam.
 */
function midSplitBeamPiece(
  beam: SupportPiece,
  fromEnd: number,
  t: number,
  pool?: SupportPiece[],
): { stump: SupportPiece; remainder: SupportPiece } | null {
  if (!beam.memberEnds || beam.memberEnds.length < 2) return null;
  const nearPos = beam.memberEnds[fromEnd].pos;
  const farPos = beam.memberEnds[1 - fromEnd].pos;
  const len = nearPos.distanceTo(farPos);
  if (len < 1e-4 || t <= 1e-4 || t >= 1 - 1e-4) return null;

  const cut = nearPos.clone().lerp(farPos, t);
  const r = beam.beamRadius ?? JOINT_PIN_RADIUS_M;
  const pinR = beamFitPinRadius(r);
  const chainId = beam.chainId ?? nextChainId(nearPos, farPos);
  const baseIndex = beam.chainIndex ?? 0;

  // Unit vector along memberEnds[0] → [1] (chain pin direction).
  const axis01 = new Vector3()
    .subVectors(beam.memberEnds[1].pos, beam.memberEnds[0].pos)
    .normalize();

  const nearCap = beam.footerCapEnd?.[fromEnd] === true;
  const farCap = beam.footerCapEnd?.[1 - fromEnd] === true;
  const stumpMesh = cylinderMesh(nearPos, cut, r, r, { horizontalCapA: nearCap });
  const remMesh = cylinderMesh(cut, farPos, r, r, { horizontalCapB: farCap });

  let stump: SupportPiece;
  let remainder: SupportPiece;

  if (fromEnd === 0) {
    // Hub/start side: stump (idx) -pin→ remainder (idx+1)
    const along = new Vector3().subVectors(farPos, nearPos).normalize();
    stump = {
      mesh: stumpMesh,
      points: meshPoints(stumpMesh),
      s: beam.s,
      beamRadius: r,
      chainId,
      chainIndex: baseIndex,
      memberEnds: [
        { pos: nearPos.clone(), along: along.clone().multiplyScalar(-1) },
        { pos: cut.clone(), along: along.clone() },
      ],
      chainSocket: beam.chainSocket,
      chainPin: { origin: cut.clone(), dir: axis01.clone(), pinRadius: pinR },
      hubPins: beam.hubPins,
      trackPin: beam.trackPin,
      footerCapEnd: [nearCap, false],
    };
    remainder = {
      mesh: remMesh,
      points: meshPoints(remMesh),
      s: beam.s,
      beamRadius: r,
      chainId,
      chainIndex: baseIndex + 1,
      memberEnds: [
        { pos: cut.clone(), along: along.clone().multiplyScalar(-1) },
        { pos: farPos.clone(), along: along.clone() },
      ],
      chainSocket: { origin: cut.clone(), dir: axis01.clone(), pinRadius: pinR },
      chainPin: beam.chainPin,
      footerCapEnd: [false, farCap],
    };
  } else {
    // Hub/end side: remainder (idx) -pin→ stump (idx+1)
    const along = new Vector3().subVectors(nearPos, farPos).normalize(); // far → near
    remainder = {
      mesh: remMesh,
      points: meshPoints(remMesh),
      s: beam.s,
      beamRadius: r,
      chainId,
      chainIndex: baseIndex,
      memberEnds: [
        { pos: farPos.clone(), along: along.clone().multiplyScalar(-1) },
        { pos: cut.clone(), along: along.clone() },
      ],
      chainSocket: beam.chainSocket,
      chainPin: { origin: cut.clone(), dir: axis01.clone(), pinRadius: pinR },
      footerCapEnd: [farCap, false],
    };
    stump = {
      mesh: stumpMesh,
      points: meshPoints(stumpMesh),
      s: beam.s,
      beamRadius: r,
      chainId,
      chainIndex: baseIndex + 1,
      memberEnds: [
        { pos: cut.clone(), along: along.clone().multiplyScalar(-1) },
        { pos: nearPos.clone(), along: along.clone() },
      ],
      chainSocket: { origin: cut.clone(), dir: axis01.clone(), pinRadius: pinR },
      chainPin: beam.chainPin,
      hubPins: beam.hubPins,
      trackPin: beam.trackPin,
      footerCapEnd: [false, nearCap],
    };
  }

  if (pool) {
    for (const p of pool) {
      if (p === beam) continue;
      if (p.chainId === chainId && p.chainIndex != null && p.chainIndex > baseIndex) {
        p.chainIndex += 1;
      }
    }
  }

  return { stump, remainder };
}

function pad(n: number): string {
  return String(n).padStart(3, '0');
}

function nonemptyMesh(mesh: SweepMesh | undefined): mesh is SweepMesh {
  return !!mesh && mesh.indices.length > 0;
}

function atomMeshFromPiece(piece: SupportPiece): SweepMesh | null {
  const meshes = [piece.mesh, ...(piece.additives ?? [])].filter(nonemptyMesh);
  if (meshes.length === 0) return null;
  return meshes.length === 1 ? meshes[0] : mergeMeshes(meshes);
}

/** Snapshot every split fragment before they were unioned into print chunks. */
function collectPrintAtoms(
  trackChunks: TrackChunkDraft[],
  onTrackByChunk: SupportPiece[][],
  supportGroups: SupportPiece[][],
): PrintAtom[] {
  const atoms: PrintAtom[] = [];
  let n = 0;
  const push = (chunkId: string, kind: PrintPart['kind'], mesh: SweepMesh | null): void => {
    if (!mesh || mesh.indices.length === 0) return;
    n += 1;
    atoms.push({ id: `atom-${pad(n)}`, chunkId, kind, mesh });
  };

  for (let ci = 0; ci < trackChunks.length; ci++) {
    const chunkId = `track-${pad(ci + 1)}`;
    const meshes = trackChunks[ci].meshes.filter(nonemptyMesh);
    push(
      chunkId,
      'track',
      meshes.length === 0 ? null : meshes.length === 1 ? meshes[0] : mergeMeshes(meshes),
    );
    for (const piece of onTrackByChunk[ci] ?? []) {
      push(chunkId, 'support', atomMeshFromPiece(piece));
    }
  }
  for (let si = 0; si < supportGroups.length; si++) {
    const chunkId = `support-${pad(si + 1)}`;
    for (const piece of supportGroups[si]) {
      push(chunkId, 'support', atomMeshFromPiece(piece));
    }
  }
  return atoms;
}

function fmtObb(obb: ObbMm): string {
  return `${obb.x.toFixed(0)}×${obb.y.toFixed(0)}×${obb.z.toFixed(0)} mm`;
}
